import * as THREE from 'three/webgpu';
import {
  type FeatureFace,
  type FeatureLine,
  type FeaturePoint,
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint
} from '../../mesh/feature';
import {isImportedTopologyProxy} from '../../imported-model/selection/imported-topology-proxy-metadata';
import {IMPORTED_BREP_MM_TO_RENDER_SPACE_SCALE} from '../../imported-model/topology/imported-model-topology-space';
import {projectPointOnFeatureSurface} from '../shared/projectPointOnFeatureSurface';
import {type DatumAxis, isDatumAxis} from '../DatumAxis';
import type {DatumPlane} from '../DatumPlane';
import type {DatumPoint} from '../DatumPoint';
import {isDatumPoint} from '../DatumPoint';
import {ReferenceCreationContext} from '../ReferenceCreationContext';
import type {ReferencePlaneController} from './ReferencePlaneController';
import {
  applyReferencePlaneAutosizeMargin,
  REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE,
} from './ReferencePlaneDisplaySizePolicy';
import {
  DEFAULT_PLANE_TOLERANCE,
  PlaneConstraintType,
  PlaneProjectionType,
  PlaneReferenceRole,
  PlaneSolveStatus,
  type PlaneTolerancePolicy,
  ReferenceDiagnosticSeverity,
  ReferenceEntitySemanticType,
} from '../ReferenceFeatureTypes';

export {
  DEFAULT_PLANE_TOLERANCE,
  PlaneConstraintType,
  PlaneProjectionType,
  PlaneReferenceRole,
  PlaneSolveStatus,
  ReferenceDiagnosticSeverity,
} from '../ReferenceFeatureTypes';
export type { PlaneTolerancePolicy } from '../ReferenceFeatureTypes';

/**
 * 基准面本地实体引用。
 */
export interface PlaneEntityReference {
  /** 实体 ID。 */
  entityId: string;
  /** 实体类型。 */
  entityType: ReferenceEntitySemanticType;
  /** 槽位标识。 */
  slot: string;

    /** 选择层解析出的拓扑命中快照。 */
  topologyPick?: ResolvedTopologyPick | null;
  /** 装配体导入拓扑稳定引用。 */
  assemblyTopologyRef?: AssemblyTopologySelectionRef | null;

}


/**
 * 单个参考定义。
 */
export interface PlaneFeatureReference {
  /** 引用在基准面三槽位中的角色。 */
  role: PlaneReferenceRole;
  /** 引用实体的稳定定位信息。 */
  entityRef: PlaneEntityReference;
  /** 引用实体的语义类型快照。 */
  entityKind: ReferenceEntitySemanticType;
  /** 当前引用驱动的约束类型。 */
  constraint: PlaneConstraintType;
  /** 当前引用关联的距离或角度标量；无标量约束时为 `null`。 */
  scalar: number | null;
  /** 控制求解方向、偏移侧与特殊约束语义的标志集合。 */
  flags: {
    /** 是否翻转方向类约束的方向。 */
    reverseDirection?: boolean;
    /** 是否翻转输出基准面的法向。 */
    reverseNormal?: boolean;
    /** 是否翻转距离偏移或中分面的输出侧。 */
    reverseOffset?: boolean;
    /** 是否按中分面语义求解。 */
    midPlane?: boolean;
    /** 是否将输出平面原点约束到曲线参考上。 */
    originOnCurve?: boolean;
  };
}

/**
 * 基准面特征定义。
 * 说明：
 * 1. 该结构用于特征持久化、编辑回填与重建；
 * 2. 不保存一次性静态平面，而保存“可复算约束定义”。
 */
export interface PlaneFeatureDefinition {
  /** 基准面特征 ID。 */
  id: string;
  /** 参与求解的引用定义集合。 */
  refs: PlaneFeatureReference[];
  /** 曲面投影策略；非曲面投影场景可为 `null`。 */
  projectionType: PlaneProjectionType | null;
  /** 当前激活候选解索引；未指定时由求解器选择默认解。 */
  solutionIndex: number | null;
  /** 多基准面输出数量。 */
  planeCount: number;
  /** 当前定义最近一次求解状态。 */
  solveStatus: PlaneSolveStatus;
  /** 定义结构版本号，用于后续持久化兼容迁移。 */
  version: number;
}



/**
 * 平面坐标系结果。
 */
export interface PlaneFrame {
  /** 平面原点。 */
  origin: THREE.Vector3;
  /** 平面单位法向。 */
  normal: THREE.Vector3;
  /** 平面局部 X 方向。 */
  xDir: THREE.Vector3;
  /** 平面局部 Y 方向。 */
  yDir: THREE.Vector3;
  /** 平面显示宽度（可选，仅用于前端可视化面片尺寸提示）。 */
  displayWidth?: number;
  /** 平面显示高度（可选，仅用于前端可视化面片尺寸提示）。 */
  displayHeight?: number;
}

/**
 * 可编辑字段枚举。
 */
export enum PlaneEditableField {
  /** 距离值可编辑。 */
  Distance = 'distance',
  /** 角度值可编辑。 */
  Angle = 'angle',
  /** 平面数量可编辑。 */
  PlaneCount = 'planeCount',
  /** 候选解索引可编辑。 */
  SolutionIndex = 'solutionIndex',
  /** 投影类型可编辑。 */
  ProjectionType = 'projectionType',
}

/**
 * 求解诊断信息。
 */
export interface PlaneDiagnostic {
  /** 诊断码。 */
  code: string;
  /** 面向用户或调试输出的诊断消息。 */
  message: string;
  /** 诊断严重级别。 */
  severity: ReferenceDiagnosticSeverity;
}

/**
 * 基准面求解结果。
 */
export interface PlaneFeatureResult {
  /** 求解状态。 */
  solveStatus: PlaneSolveStatus;
  /** 求解状态展示文本。 */
  statusText: string;
  /** 是否只有唯一候选解。 */
  uniqueSolution: boolean;
  /** 候选解总数。 */
  solutionCount: number;
  /** 当前激活候选解索引。 */
  activeSolutionIndex: number;
  /** 求解出的基准面坐标系集合。 */
  planes: PlaneFrame[];
  /** 当前结果允许编辑的字段集合。 */
  editableFields: PlaneEditableField[];
  /** 当前引用组合允许切换的约束集合。 */
  availableConstraints: PlaneConstraintType[];
  /** 当前引用组合允许切换的曲面投影策略集合。 */
  availableProjectionTypes: PlaneProjectionType[];
  /** 求解过程中产生的诊断信息。 */
  diagnostics: PlaneDiagnostic[];
}


/**
 * 已解析参考实体。
 * 直接使用三个具体的场景对象类，取代原有的匿名数据结构，保证类型安全与可追踪性。
 */
export type PlaneResolvedEntity = FeatureFace | FeatureLine | FeaturePoint | DatumAxis | DatumPoint | DatumPlane;
/**
 * 线性判定容差。
 */
const LINEAR_EPSILON = 1e-6;

/**
 * 线参考场景下允许的约束集合。
 */
const LINE_REFERENCE_AVAILABLE_CONSTRAINTS: PlaneConstraintType[] = [
  PlaneConstraintType.Perpendicular,
  PlaneConstraintType.Coincident,
  PlaneConstraintType.Project,
];

/**
 * 基准面显示尺寸调试使用的场景单位缩放因子。
 *
 * 设计说明：
 * 1. 当前值只服务于显示尺寸日志与导入拓扑显示策略，不再参与 Distance 标量求解；
 * 2. 保留该常量可继续沿用装配体导入几何的毫米级显示诊断口径；
 * 3. 基准面 Distance 的真实求解输入已经在 UI/命令边界统一换算为米制。
 */
const REFERENCE_PLANE_MM_TO_SCENE_UNIT_SCALE = IMPORTED_BREP_MM_TO_RENDER_SPACE_SCALE;

/**
 * 线参考几何数据。
 */
interface LineReferenceGeometry {
  /** 参考点。 */
  point: THREE.Vector3;
  /** 方向向量。 */
  direction: THREE.Vector3;
  /** 可选起点。 */
  startPoint?: THREE.Vector3;
  /** 可选终点。 */
  endPoint?: THREE.Vector3;
}

/**
 * 平面参考几何数据。
 */
interface PlaneReferenceGeometry {
  /** 平面上一点。 */
  point: THREE.Vector3;
  /** 平面法向。 */
  normal: THREE.Vector3;
}

/**
 * 角度求解附加选项。
 */
interface SolveAngleOptions {
  /** 是否保留垂直关系作为角度零基准。 */
  preservePerpendicularState?: boolean;
}

/**
 * 角度求解参考集合。
 */
interface AngleReferenceBundle {
  /** 平面实体。 */
  planeEntity: PlaneResolvedEntity;
  /** 线性实体。 */
  lineEntity: PlaneResolvedEntity;
  /** 作为旋转基准的平面。 */
  basePlane: PlanePointNormalGeometry;
  /** 旋转轴方向。 */
  axisDirection: THREE.Vector3;
}

/**
 * 点合并判定容差。
 */
const POINT_MERGE_EPSILON = 1e-9;
/**
 * 三点法向退化容差。
 */
const THREE_POINT_NORMAL_EPSILON = 1e-9;

/**
 * 平面方程表达。
 */
interface PlaneEquation {
  /** 单位法向。 */
  normal: THREE.Vector3;
  /** 平面方程常数项。 */
  d: number;
  /** 平面上一点。 */
  point: THREE.Vector3;
}

/**
 * 相交中分面候选。
 */
interface IntersectingMidPlaneCandidate {
  /** 平面坐标系结果。 */
  frame: PlaneFrame;
  /** 平面法向。 */
  normal: THREE.Vector3;
  /** 平面方程常数项。 */
  d: number;
}

/**
 * 角度旋转轴信息。
 */
interface AngleRotationAxis {
  /** 轴上一点。 */
  point: THREE.Vector3;
  /** 轴方向。 */
  direction: THREE.Vector3;
}

/**
 * 线段几何数据。
 */
interface LineSegmentGeometry {
  /** 起点。 */
  start: THREE.Vector3;
  /** 终点。 */
  end: THREE.Vector3;
  /** 中点。 */
  midpoint: THREE.Vector3;
  /** 长度。 */
  length: number;
}

/**
 * 曲面投影候选。
 */
interface SurfaceProjectionCandidate {
  /** 曲面上的最近点。 */
  point: THREE.Vector3;
  /** 最近点处的局部法向。 */
  normal: THREE.Vector3;
  /** 与输入点的平方距离。 */
  distanceSq: number;
}

/**
 * 曲面与点参考组合。
 */
interface SurfacePointReferenceBundle {
  /** 曲面参考实体。 */
  surfaceEntity: PlaneResolvedEntity;
  /** 点参考实体。 */
  pointEntity: PlaneResolvedEntity;
  /** 点参考的世界坐标。 */
  point: THREE.Vector3;
}

/**
 * 曲面与平面参考组合。
 */
interface SurfacePlaneReferenceBundle {
  /** 曲面参考实体。 */
  surfaceEntity: PlaneResolvedEntity;
  /** 平面参考实体。 */
  planeEntity: PlaneResolvedEntity;
  /** 平面参考几何。 */
  plane: PlaneReferenceGeometry;
}

/**
 * 曲面相切且垂直于平面的候选面。
 */
interface SurfacePlanePerpendicularCandidate {
  /** 平面坐标系结果。 */
  frame: PlaneFrame;
  /** 规范化后的法向。 */
  canonicalNormal: THREE.Vector3;
  /** 规范化平面方程常数。 */
  canonicalD: number;
}

/**
 * 曲面相切且经过指定点的候选面。
 */
interface SurfacePointTangentCandidate {
  /** 平面坐标系结果。 */
  frame: PlaneFrame;
  /** 曲面上的切点。 */
  surfacePoint: THREE.Vector3;
  /** 规范化后的法向。 */
  canonicalNormal: THREE.Vector3;
  /** 规范化平面方程常数。 */
  canonicalD: number;
}

/**
 * 线面垂直场景的参考组合。
 */
interface LinePlanePerpendicularReferences {
  /** 线性实体。 */
  line: PlaneResolvedEntity;
  /** 平面实体。 */
  plane: PlaneResolvedEntity;
}


/**
 * 曲面 + 平面平行约束参考组合。
 */
interface SurfacePlaneParallelReferences {
  /** 曲面参考实体。 */
  surfaceEntity: PlaneResolvedEntity;
  /** 平面参考实体。 */
  planeEntity: PlaneResolvedEntity;
  /** 平面参考几何。 */
  planeGeometry: PlaneReferenceGeometry;
}


/**
 * 线面垂直显示布局选项。
 */
interface ApplyLinePlanePerpendicularDisplayLayoutOptions {
  /** 是否保留角度场景的显示排序。 */
  preserveAngularSequence?: boolean;
}

/**
 * 曲面加平面垂直场景的显示布局结果。
 */
interface SurfacePlanePerpendicularDisplayLayout {
  /** 平面显示宽度。 */
  width: number;
  /** 平面显示高度。 */
  height: number;
  /** 调整后的显示中心。 */
  origin: THREE.Vector3;
  /** 曲面宽度方向。 */
  widthDirection: THREE.Vector3;
  /** 曲面直径方向。 */
  heightDirection: THREE.Vector3;
}

/**
 * 基准面显示尺寸策略。
 */
interface PlaneDisplaySizePolicy {
  /** 用于收集显示提示点的约束语义。 */
  effectiveConstraint: PlaneConstraintType;
  /** 显示边界外扩倍率。 */
  marginScale: number;
  /** 是否将显示中心移动到参考轮廓中心。 */
  recenterOrigin: boolean;
}

/**
 * 模型包围盒快照。
 */
interface ReferencePlaneModelBoundingBox {
  /** 包围盒最小点。 */
  min: THREE.Vector3;
  /** 包围盒最大点。 */
  max: THREE.Vector3;
}


/**
 * 基准面本地范围。
 */
interface LocalPlaneExtent {
  /** 本地 X 最小值。 */
  minX: number;
  /** 本地 X 最大值。 */
  maxX: number;
  /** 本地 Y 最小值。 */
  minY: number;
  /** 本地 Y 最大值。 */
  maxY: number;
  /** 本地宽度。 */
  width: number;
  /** 本地高度。 */
  height: number;
  /** 本地中心 X。 */
  centerX: number;
  /** 本地中心 Y。 */
  centerY: number;
}


/**
 * 基准面统一解算器。
 *
 * 设计说明：
 * 1. 将原 `features` 目录下的定义、结果、类型、容差、工具函数和子求解器统一收口到本文件；
 * 2. 基准面求解入口直接内聚到类方法中，不再额外暴露文件级入口函数；
 * 3. 后续可继续把文件级辅助函数逐步下沉为类静态/私有方法。
 */
export class ReferencePlaneSolver {

    /**
   * 基准面显示尺寸调试日志开关。
   *
   * 说明：该日志只在求解阶段输出，用于排查装配体导入拓扑的范围与单位缩放问题。
   */
  /**
   * 控制是否输出基准面显示尺寸调试日志。
   *
   * 默认为 false，生产环境建议保持关闭，仅在排查显示尺寸相关问题时临时打开。
   */
  private static readonly DISPLAY_SIZE_DEBUG_ENABLED = false;


    private readonly controller: ReferencePlaneController;

  /**
   * 创建基准面求解器。
   *
   * @param controller 基准面控制器实例。
   */
  public constructor(controller: ReferencePlaneController) {
    this.controller = controller;
  }

  /**
   * 执行基准面求解。
   *
   * @param definition 基准面定义。
   * @param creationContext 当前求解使用的创建环境。
   * @returns 求解结果。
   */
  public solve(
    definition: PlaneFeatureDefinition,
    creationContext: ReferenceCreationContext = ReferenceCreationContext.PartFeature,
  ): PlaneFeatureResult {
    const resolved = this.resolveEntities(definition);
    const tolerance = this.controller.getTolerance();

    if (resolved.length === 0) {
      return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.UnderDefined, '未选择任何可解析参考');
    }

    const constraint = ReferencePlaneSolver.inferConstraint(definition, resolved);
    const scalar = constraint === PlaneConstraintType.Angle
      ? ReferencePlaneSolver.readAngleScalar(definition)
      : ReferencePlaneSolver.readPrimaryScalar(definition);
    const solverScalar = ReferencePlaneSolver.resolveSolverScalar(constraint, scalar, creationContext);
    const preserveLinePlanePerpendicularState = (
      constraint === PlaneConstraintType.Angle
      && ReferencePlaneSolver.isLinePlaneMixedAngleConstraint(definition, resolved)
    );
    const firstRef = definition.refs.find((item) => item.role === PlaneReferenceRole.First);
    const originOnCurve = Boolean(firstRef?.flags?.originOnCurve);
    const reverseOffset = Boolean(firstRef?.flags?.reverseOffset);
    const projectionType = definition.projectionType ?? PlaneProjectionType.NearestOnSurface;

    // 约束推断完成后统一分派到对应子求解器，避免控制器侧复制判断逻辑。
    let result: PlaneFeatureResult;
    switch (constraint) {
      case PlaneConstraintType.Distance:
        result = ReferencePlaneSolver.solveDistanceOffset(resolved, solverScalar);
        break;
      case PlaneConstraintType.MidPlane:
        result = ReferencePlaneSolver.solveMidPlane(resolved, tolerance.angularEpsilon, reverseOffset);
        break;
      case PlaneConstraintType.Parallel:
        result = ReferencePlaneSolver.solveParallel(resolved);
        break;
      case PlaneConstraintType.Normal:
        result = ReferencePlaneSolver.solveNormal(resolved, originOnCurve);
        break;
      case PlaneConstraintType.Perpendicular:
        result = this.solvePerpendicularConstraintWithOrderCompatibility(resolved, originOnCurve, reverseOffset);
        break;
      case PlaneConstraintType.Angle:
        result = ReferencePlaneSolver.solveAngle(resolved, solverScalar, {
          preservePerpendicularState: preserveLinePlanePerpendicularState,
        });
        break;
      case PlaneConstraintType.Tangent:
        result = this.solveTangent(resolved, tolerance);
        break;
      case PlaneConstraintType.OnSurface:
        result = ReferencePlaneSolver.solveOnSurfaceProject(resolved, projectionType, tolerance);
        break;
      case PlaneConstraintType.Project:
        result = ReferencePlaneSolver.solveLineProjectConstraint(resolved)
          ?? ReferencePlaneSolver.solveOnSurfaceProject(resolved, projectionType, tolerance);
        break;
      case PlaneConstraintType.Coincident:
        result = ReferencePlaneSolver.solveCoincident(resolved);
        break;
      default:
        result = ReferencePlaneSolver.solveCoincident(resolved);
        break;
    }

    result = ReferencePlaneSolver.applyNormalReverse(result, definition);
    result = ReferencePlaneSolver.expandOffsetPlanes(result, definition, constraint, resolved, solverScalar);
    result = ReferencePlaneSolver.applyDisplaySizeHint(
      result,
      resolved,
      constraint,
      preserveLinePlanePerpendicularState,
      this.controller.getModelBoundingBox(),
    );

    const activeIndex = Number.isFinite(definition.solutionIndex) ? Math.max(0, Math.floor(definition.solutionIndex ?? 0)) : 0;
    /**
     * 区分"几何多候选解"与"多输出平面"两种情形：
     * - 几何多候选解（solutionCount > 1）：用户需通过候选解索引选择其中一个，显示 SolutionIndex 控件。
     * - 多输出平面（solutionCount === 1 && planes.length > 1）：由 planeCount 扩展产生，
     *   所有平面均为最终输出，不应视为候选解，不显示 SolutionIndex 控件。
     */
    if (result.solutionCount > 1) {
      const pickedIndex = Math.min(result.solutionCount - 1, activeIndex);
      result = {
        ...result,
        activeSolutionIndex: pickedIndex,
        editableFields: Array.from(new Set([...result.editableFields, PlaneEditableField.SolutionIndex])),
      };
    }

    if (result.solveStatus === PlaneSolveStatus.FullyDefined && result.planes.length > 0) {
      return {
        ...result,
        solveStatus: PlaneSolveStatus.FullyDefined,
        statusText: result.solutionCount > 1 ? '完全定义（存在多个候选解）' : '完全定义',
      };
    }

    const isPointOnlyUnderDefined = result.solveStatus === PlaneSolveStatus.UnderDefined
      && !ReferencePlaneSolver.readDirection(resolved[0])
      && Boolean(ReferencePlaneSolver.readPoint(resolved[0]));
    if (isPointOnlyUnderDefined) {
      return {
        ...result,
        solveStatus: PlaneSolveStatus.UnderDefined,
        statusText: '未完全定义',
      };
    }

    return result;
  }

  /**
   * 归一化三维向量。
   *
   * @param v 输入向量。
   * @returns 单位向量；退化时返回默认 Z 轴。
   */
  private static normalizeVec3(v: THREE.Vector3): THREE.Vector3 {
    const len = v.length();
    if (len < 1e-12) {
      return new THREE.Vector3(0, 0, 1);
    }
    return v.clone().normalize();
  }


    /**
   * 转换向量为稳定日志数组。
   *
   * @param vector 三维向量。
   * @returns 三维数值数组。
   */
  private static toDebugVec3(vector: THREE.Vector3): [number, number, number] {
    return [vector.x, vector.y, vector.z];
  }

  /**
   * 输出基准面显示尺寸计算日志。
   *
   * @param message 日志说明。
   * @param payload 调试数据。
   */
  private static logDisplaySizeDebug(message: string, payload: Record<string, unknown>): void {
    if (!ReferencePlaneSolver.DISPLAY_SIZE_DEBUG_ENABLED) {
      return;
    }
    console.log(`[基准面显示尺寸调试] ${message}`, payload);
  }

  /**
   * 判断当前求解是否包含导入拓扑代理参考。
   *
   * @param resolved 已解析引用实体集合。
   * @returns 若包含导入拓扑代理则返回 `true`。
   */
  private static hasImportedTopologyReference(resolved: PlaneResolvedEntity[]): boolean {
    return resolved.some((entity) => ReferencePlaneSolver.isImportedTopologyReferenceEntity(entity));
  }


    /**
   * 判断当前实体是否来自导入拓扑代理链路。
   *
   * @param entity 已解析实体。
   * @returns 若实体是导入拓扑代理则返回 `true`。
   */
  private static isImportedTopologyReferenceEntity(entity: PlaneResolvedEntity | null | undefined): boolean {
    if (!entity) {
      return false;
    }
    if (!(entity instanceof THREE.Object3D)) {
      return false;
    }
    return isImportedTopologyProxy(entity);
  }

  /**
   * 解析真正参与几何求解的标量。
   *
   * 设计说明：
   * 1. 装配体环境中的距离值保持 PropertyManager 的毫米语义，避免导入拓扑工作流被场景单位污染；
   * 2. 特征环境中的距离值直接使用场景米制，不再执行额外缩放；
   * 3. 角度标量不是线性尺寸，不能参与该换算。
   *
   * @param constraint 当前约束。
   * @param scalar 原始标量。
   * @param creationContext 当前求解环境。
   * @returns 求解器使用的标量。
   */
  private static resolveSolverScalar(
    constraint: PlaneConstraintType,
    scalar: number,
    creationContext: ReferenceCreationContext,
  ): number {
    void creationContext;
    if (!Number.isFinite(scalar)) {
      return 0;
    }
    if (constraint !== PlaneConstraintType.Distance) {
      return scalar;
    }
    return scalar;
  }

  /**
   * 为平面坐标系挑选稳定参考轴。
   *
   * @param normal 平面法向。
   * @returns 参考轴方向。
   */
  private static pickReferenceAxis(normal: THREE.Vector3): THREE.Vector3 {
    const n = ReferencePlaneSolver.normalizeVec3(normal);
    if (Math.abs(n.z) < 0.9) {
      return new THREE.Vector3(0, 0, 1);
    }
    return new THREE.Vector3(1, 0, 0);
  }

  /**
   * 构建平面坐标系。
   *
   * @param origin 平面原点。
   * @param normal 平面法向。
   * @returns 平面坐标系。
   */
  private static buildPlaneFrame(origin: THREE.Vector3, normal: THREE.Vector3): PlaneFrame {
    const n = ReferencePlaneSolver.normalizeVec3(normal);
    const ref = ReferencePlaneSolver.pickReferenceAxis(n);
    const xDir = ReferencePlaneSolver.normalizeVec3(ref.clone().cross(n));
    const yDir = ReferencePlaneSolver.normalizeVec3(n.clone().cross(xDir));
    return {
      origin: origin.clone(),
      normal: n,
      xDir,
      yDir,
    };
  }

  /**
   * 计算点在线上的正交投影。
   *
   * @param point 待投影点。
   * @param linePoint 线上一点。
   * @param lineDir 线方向。
   * @returns 投影点。
   */
  private static projectPointOnLine(point: THREE.Vector3, linePoint: THREE.Vector3, lineDir: THREE.Vector3): THREE.Vector3 {
    const dir = ReferencePlaneSolver.normalizeVec3(lineDir);
    const v = point.clone().sub(linePoint);
    const t = v.dot(dir);
    return linePoint.clone().addScaledVector(dir, t);
  }

  /**
   * 绕轴旋转向量。
   *
   * @param vector 待旋转向量。
   * @param axis 旋转轴。
   * @param angleRad 弧度角。
   * @returns 旋转后的向量。
   */
  private static rotateAroundAxis(vector: THREE.Vector3, axis: THREE.Vector3, angleRad: number): THREE.Vector3 {
    const k = ReferencePlaneSolver.normalizeVec3(axis);
    return vector.clone().applyAxisAngle(k, angleRad);
  }

  /**
   * 读取实体代表点。
   *
   * @param entity 已解析实体。
   * @returns 代表点；无法读取时返回 `null`。
   */
  private static readPoint(entity: PlaneResolvedEntity | null | undefined): THREE.Vector3 | null {
    if (!entity) {
      return null;
    }


      if (isDatumAxis(entity)) {
      return entity.getWorldMidpoint();
    }

      if (isDatumPoint(entity)) {
      return entity.getWorldPoint();
    }
    if ('getMesh' in entity && typeof entity.getMesh === 'function' && 'normal' in entity && 'originWorld' in entity) {
      return entity.originWorld.clone();
    }

    if (isFeaturePoint(entity)) {
      const pos = entity.getWorldPoint();
      return pos.clone();
    }

    if (isFeatureLine(entity)) {
      const pos = entity.getWorldMidpoint();
      if (pos) {
        return pos.clone();
      }
      const fallback = new THREE.Vector3();
      entity.getWorldPosition(fallback);
      return fallback;
    }

    if (isFeatureFace(entity)) {
      const pos = entity.getWorldPoint();
      return pos.clone();
    }

    return null;
  }

  /**
   * 读取线性实体方向。
   *
   * @param entity 已解析实体。
   * @returns 方向向量；无法读取时返回 `null`。
   */
  private static readDirection(entity: PlaneResolvedEntity | null | undefined): THREE.Vector3 | null {

      if (isDatumAxis(entity)) {
      return entity.getWorldDirection();
    }

      if (!isFeatureLine(entity)) {
      return null;
    }
    const d = entity.getWorldDirection();
    return d ? d.clone() : null;
  }

  /**
   * 判断实体是否为正式基准面。
   *
   * @param entity 已解析实体。
   * @returns 若为正式基准面返回 `true`。
   */
  private static isDatumPlaneEntity(entity: PlaneResolvedEntity | null | undefined): entity is DatumPlane {
    return Boolean(
      entity
      && 'getMesh' in entity
      && typeof entity.getMesh === 'function'
      && 'normal' in entity
      && 'originWorld' in entity
    );
  }

  /**
   * 判断实体是否为非平面曲面。
   *
   * @param entity 已解析实体。
   * @returns 若为圆柱面、圆锥面、样条面等非平面曲面返回 `true`。
   */
  private static isNonPlanarSurfaceEntity(entity: PlaneResolvedEntity | null | undefined): boolean {
    if (!isFeatureFace(entity)) {
      return false;
    }
    const isFeatureSurface = Reflect.get(entity, 'isFeatureSurface') === true;
    const surfaceKind = Reflect.get(entity, 'surfaceKind');
    return isFeatureSurface && surfaceKind !== 'plane';
  }

  /**
   * 判断实体是否为可参与“平面类”约束的参考。
   *
   * @param entity 已解析实体。
   * @returns 若为正式基准面或平面面返回 `true`。
   */
  private static isPlanarPlaneEntity(entity: PlaneResolvedEntity | null | undefined): boolean {
    if (ReferencePlaneSolver.isDatumPlaneEntity(entity)) {
      return true;
    }
    if (!isFeatureFace(entity)) {
      return false;
    }
    return !ReferencePlaneSolver.isNonPlanarSurfaceEntity(entity);
  }

  /**
   * 判断实体是否为点类参考。
   *
   * @param entity 已解析实体。
   * @returns 若为点/顶点/基准点返回 `true`。
   */
  private static isPointReferenceEntity(entity: PlaneResolvedEntity | null | undefined): boolean {
    if (!entity) {
      return false;
    }
    if (ReferencePlaneSolver.readDirection(entity) !== null) {
      return false;
    }
    if (ReferencePlaneSolver.isPlanarPlaneEntity(entity) || ReferencePlaneSolver.isNonPlanarSurfaceEntity(entity)) {
      return false;
    }
    return ReferencePlaneSolver.readPoint(entity) !== null;
  }

  /**
   * 读取平面实体的法向与基点。
   *
   * @param entity 已解析实体。
   * @returns 平面信息；无法读取时返回 `null`。
   */
  private static readPlane(entity: PlaneResolvedEntity | null | undefined): PlanePointNormalGeometry | null {
    if (ReferencePlaneSolver.isDatumPlaneEntity(entity)) {
      return {
        normal: entity.normal.clone(),
        point: entity.originWorld.clone(),
      };
    }
    if (!isFeatureFace(entity)) {
      return null;
    }
    const plane = entity.getWorldPlane();
    return {
      normal: plane.normal.clone(),
      point: plane.point.clone(),
    };
  }

  /**
   * 读取线实体端点。
   *
   * @param entity 已解析实体。
   * @returns 端点信息；无法读取时返回 `null`。
   */
  private static readLineEndpoints(entity: PlaneResolvedEntity | null | undefined): LineSegmentEndpoints | null {

      if (isDatumAxis(entity)) {
      return entity.getWorldEndpoints();
    }

      if (!isFeatureLine(entity)) {
      return null;
    }
    // 委托给 FeatureLine.getWorldEndpoints() 封装实现，避免直接读取几何属性
    const pts = entity.getWorldEndpoints();
    if (!pts) {
      return null;
    }
    const { start: s, end: e } = pts;
    return {
      start: s.clone(),
      end: e.clone(),
    };
  }

  /**
   * 读取面外环顶点。
   *
   * @param entity 已解析实体。
   * @returns 外环顶点集合。
   */
  private static readFaceOuterLoop(entity: PlaneResolvedEntity | null | undefined): THREE.Vector3[] {
    if (entity && 'getMesh' in entity && typeof entity.getMesh === 'function' && 'normal' in entity) {
      return [];
    }
    if (!isFeatureFace(entity)) {
      return [];
    }
    return entity.getWorldBoundaryVertices();
  }


    /**
   * 解析曲面 + 平面平行约束参考组合。
   *
   * @param resolved 已解析实体。
   * @returns 参考组合；无法解析时返回 `null`。
   */
  private static resolveSurfacePlaneParallelReferences(
    resolved: PlaneResolvedEntity[]
  ): SurfacePlaneParallelReferences | null {
    const first = resolved[0];
    const second = resolved[1];
    if (!first || !second) {
      return null;
    }

    if (ReferencePlaneSolver.isNonPlanarSurfaceEntity(first) && !ReferencePlaneSolver.isNonPlanarSurfaceEntity(second)) {
      const planeGeometry = ReferencePlaneSolver.resolvePlaneReferenceGeometry(second);
      if (planeGeometry) {
        return { surfaceEntity: first, planeEntity: second, planeGeometry };
      }
    }

    if (ReferencePlaneSolver.isNonPlanarSurfaceEntity(second) && !ReferencePlaneSolver.isNonPlanarSurfaceEntity(first)) {
      const planeGeometry = ReferencePlaneSolver.resolvePlaneReferenceGeometry(first);
      if (planeGeometry) {
        return { surfaceEntity: second, planeEntity: first, planeGeometry };
      }
    }

    return null;
  }

  /**
   * 读取曲面全部世界坐标顶点。
   *
   * @param surfaceEntity 曲面实体。
   * @returns 世界坐标顶点集合。
   */
  private static readSurfaceWorldVertices(surfaceEntity: PlaneResolvedEntity): THREE.Vector3[] {
    if (!isFeatureFace(surfaceEntity)) {
      return [];
    }

    const geometry = surfaceEntity.geometry;
    const positionAttribute = geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute) || positionAttribute.itemSize < 3) {
      return [];
    }

    surfaceEntity.updateWorldMatrix(true, false);
    const vertices: THREE.Vector3[] = [];
    const visited = new Set<number>();
    const geometryIndex = geometry.getIndex();

    const appendVertex = (vertexIndex: number): void => {
      if (visited.has(vertexIndex)) {
        return;
      }
      visited.add(vertexIndex);
      const worldPoint = new THREE.Vector3().fromBufferAttribute(positionAttribute, vertexIndex);
      worldPoint.applyMatrix4(surfaceEntity.matrixWorld);
      vertices.push(worldPoint);
    };

    if (geometryIndex) {
      for (let indexCursor = 0; indexCursor < geometryIndex.count; indexCursor += 1) {
        appendVertex(geometryIndex.getX(indexCursor));
      }
      return vertices;
    }

    for (let vertexIndex = 0; vertexIndex < positionAttribute.count; vertexIndex += 1) {
      appendVertex(vertexIndex);
    }
    return vertices;
  }


    /**
   * 判断对象是否位于指定祖先对象的子树内。
   *
   * @param object 需要判断的对象。
   * @param ancestor 期望的祖先对象。
   * @returns 位于该祖先子树内时返回 `true`。
   */
  private static isObjectAttachedUnderAncestor(object: THREE.Object3D, ancestor: THREE.Object3D): boolean {
    let current: THREE.Object3D | null = object;
    while (current) {
      if (current === ancestor) {
        return true;
      }
      current = current.parent;
    }
    return false;
  }

  /**
   * 解析基准面读取特征面局部面片点时应使用的世界矩阵。
   *
   * 设计说明：
   * 1. 该函数只服务基准面显示尺寸，不改变 FeatureFace 自身公共读取语义；
   * 2. 导入拓扑面代理可能只是结果体缓存对象，并未挂回结果体子树；
   * 3. 未挂载代理的局部几何仍属于所属结果体坐标系，因此需要使用 owning result mesh 的世界矩阵。
   *
   * @param face 特征面代理。
   * @returns 局部面片点转世界坐标的矩阵。
   */
  private static resolveReferencePlaneFeatureFaceWorldMatrix(face: FeatureFace): THREE.Matrix4 {
    const owningResultMesh = face.getOwningResultMesh();
    if (owningResultMesh && !ReferencePlaneSolver.isObjectAttachedUnderAncestor(face, owningResultMesh)) {
      owningResultMesh.updateWorldMatrix(true, false);
      return owningResultMesh.matrixWorld;
    }

    if (face.parent) {
      face.updateWorldMatrix(true, false);
      return face.matrixWorld;
    }

    if (owningResultMesh) {
      owningResultMesh.updateWorldMatrix(true, false);
      return owningResultMesh.matrixWorld;
    }

    face.updateWorldMatrix(true, false);
    return face.matrixWorld;
  }

  /**
   * 读取基准面显示尺寸专用的特征面面片顶点。
   *
   * 设计说明：
   * 1. 环形端面、不规则平面可能只有稀疏边界线点，直接用外环会把基准面压成长条；
   * 2. 这里额外读取当前 FeatureFace 代理几何的全部去重顶点，只用于基准面显示尺寸；
   * 3. 不复用也不修改通用 `readSurfaceWorldVertices()`，避免影响曲面相切、配合、测量等其它链路。
   *
   * @param entity 已解析实体。
   * @returns 基准面显示尺寸参考点集合。
   */
  private static readReferencePlaneFeatureFaceSurfaceVertices(entity: PlaneResolvedEntity): THREE.Vector3[] {
    if (!isFeatureFace(entity)) {
      return [];
    }

    const positionAttribute = entity.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute) || positionAttribute.itemSize < 3) {
      return [];
    }

    const worldMatrix = ReferencePlaneSolver.resolveReferencePlaneFeatureFaceWorldMatrix(entity);
    const vertices: THREE.Vector3[] = [];
    const visited = new Set<number>();
    const indexAttribute = entity.geometry.getIndex();

    /**
     * 追加指定顶点的世界坐标。
     *
     * @param vertexIndex 顶点索引。
     */
    const appendVertex = (vertexIndex: number): void => {
      if (!Number.isInteger(vertexIndex) || vertexIndex < 0 || vertexIndex >= positionAttribute.count) {
        return;
      }
      if (visited.has(vertexIndex)) {
        return;
      }
      visited.add(vertexIndex);
      const worldPoint = new THREE.Vector3().fromBufferAttribute(positionAttribute, vertexIndex);
      worldPoint.applyMatrix4(worldMatrix);
      vertices.push(worldPoint);
    };

    if (indexAttribute instanceof THREE.BufferAttribute) {
      for (let indexCursor = 0; indexCursor < indexAttribute.count; indexCursor += 1) {
        appendVertex(Number(indexAttribute.getX(indexCursor)));
      }
      return vertices;
    }

    for (let vertexIndex = 0; vertexIndex < positionAttribute.count; vertexIndex += 1) {
      appendVertex(vertexIndex);
    }
    return vertices;
  }


    /**
   * 读取正式基准面面片的世界坐标顶点。
   *
   * 设计说明：
   * 1. 正式基准面不是 `FeatureFace`，不能走普通面的外环与曲面顶点读取链路；
   * 2. 新基准面以正式基准面为参考时，应继承参考基准面的显示尺寸，而不是回退到全局默认尺寸；
   * 3. 这里只读取 `planeMesh` 的几何顶点，避免边框、标签、调试 helper 影响显示范围。
   *
   * @param datumPlane 正式基准面实体。
   * @returns 基准面面片世界坐标顶点集合。
   */
  private static readDatumPlaneWorldVertices(datumPlane: DatumPlane): THREE.Vector3[] {
    const mesh = datumPlane.getMesh();
    const positionAttribute = mesh.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute) || positionAttribute.itemSize < 3) {
      return [];
    }

    // 读取前强制刷新父级 group 与 mesh 的世界矩阵，保证 group 封装后的方向和位移都被纳入计算。
    mesh.updateWorldMatrix(true, false);
    const vertices: THREE.Vector3[] = [];
    const visited = new Set<number>();
    const geometryIndex = mesh.geometry.getIndex();

    /**
     * 追加一个局部顶点对应的世界坐标。
     *
     * @param vertexIndex 顶点索引。
     */
    const appendVertex = (vertexIndex: number): void => {
      if (visited.has(vertexIndex)) {
        return;
      }
      visited.add(vertexIndex);
      const worldPoint = new THREE.Vector3().fromBufferAttribute(positionAttribute, vertexIndex);
      worldPoint.applyMatrix4(mesh.matrixWorld);
      vertices.push(worldPoint);
    };

    if (geometryIndex) {
      for (let indexCursor = 0; indexCursor < geometryIndex.count; indexCursor += 1) {
        appendVertex(geometryIndex.getX(indexCursor));
      }
      return vertices;
    }

    for (let vertexIndex = 0; vertexIndex < positionAttribute.count; vertexIndex += 1) {
      appendVertex(vertexIndex);
    }
    return vertices;
  }


    /**
   * 基于目标平面法向构造曲面支撑平面候选。
   *
   * @param surfaceEntity 曲面实体。
   * @param planeGeometry 目标平面几何。
   * @returns 候选平面集合。
   */
  private static buildSurfaceParallelSupportFrames(
    surfaceEntity: PlaneResolvedEntity,
    planeGeometry: PlaneReferenceGeometry
  ): PlaneFrame[] {
    const vertices = ReferencePlaneSolver.readSurfaceWorldVertices(surfaceEntity);
    if (vertices.length === 0) {
      return [];
    }

    const planeNormal = ReferencePlaneSolver.normalizeVec3(planeGeometry.normal);
    let minSupport = Number.POSITIVE_INFINITY;
    let maxSupport = Number.NEGATIVE_INFINITY;
    let minPoint: THREE.Vector3 | null = null;
    let maxPoint: THREE.Vector3 | null = null;

    vertices.forEach((vertex) => {
      const support = vertex.dot(planeNormal);
      if (support < minSupport) {
        minSupport = support;
        minPoint = vertex.clone();
      }
      if (support > maxSupport) {
        maxSupport = support;
        maxPoint = vertex.clone();
      }
    });

    const referenceSupport = planeGeometry.point.dot(planeNormal);
    const candidates = [
      minPoint ? { point: minPoint, support: minSupport } : null,
      maxPoint ? { point: maxPoint, support: maxSupport } : null,
    ].filter((item): item is { point: THREE.Vector3; support: number } => Boolean(item));

    if (candidates.length === 2 && Math.abs(candidates[0].support - candidates[1].support) <= LINEAR_EPSILON) {
      return [ReferencePlaneSolver.buildPlaneFrame(candidates[0].point, planeNormal)];
    }

    candidates.sort((left, right) => {
      const distanceDelta = Math.abs(left.support - referenceSupport) - Math.abs(right.support - referenceSupport);
      if (Math.abs(distanceDelta) > LINEAR_EPSILON) {
        return distanceDelta;
      }
      return left.support - right.support;
    });

    return candidates.map((candidate) => ReferencePlaneSolver.buildPlaneFrame(candidate.point, planeNormal));
  }


    /**
   * 读取缓冲属性中的向量。
   *
   * @param attribute 缓冲属性。
   * @param index 顶点索引。
   * @returns 向量值。
   */
  private static readBufferVector3(attribute: THREE.BufferAttribute, index: number): THREE.Vector3 {
    return new THREE.Vector3(
      attribute.getX(index),
      attribute.getY(index),
      attribute.getZ(index),
    );
  }

  /**
   * 读取面代理中指定顶点的世界坐标。
   *
   * @param entity 面代理对象。
   * @param attribute 顶点位置属性。
   * @param index 顶点索引。
   * @returns 世界坐标。
   */
  private static readSurfaceVertexWorldPosition(
    entity: FeatureFace,
    attribute: THREE.BufferAttribute,
    index: number,
  ): THREE.Vector3 {
    const localPoint = ReferencePlaneSolver.readBufferVector3(attribute, index);
    return localPoint.applyMatrix4(entity.matrixWorld);
  }

  /**
   * 读取曲面参考与点参考。
   *
   * @param resolved 已解析实体集合。
   * @returns 成功时返回曲面与点组合，否则返回 `null`。
   */
  private static resolveSurfacePointReferenceBundle(
    resolved: PlaneResolvedEntity[],
  ): SurfacePointReferenceBundle | null {
    const surfaceEntity = resolved.find((entity) => ReferencePlaneSolver.isNonPlanarSurfaceEntity(entity)) ?? null;
    if (!surfaceEntity) {
      return null;
    }

    const pointEntity = resolved.find((entity) => (
      entity !== surfaceEntity && ReferencePlaneSolver.isPointReferenceEntity(entity)
    )) ?? null;
    if (!pointEntity) {
      return null;
    }

    const point = ReferencePlaneSolver.readPoint(pointEntity);
    if (!point) {
      return null;
    }

    return {
      surfaceEntity,
      pointEntity,
      point,
    };
  }

  /**
   * 基于曲面三角网格求最近点及其局部法向。
   *
   * 设计说明：
   * 1. 当前主线程没有 Puppy CAD 内核 原生曲面求值器，因此这里以面代理的三角化结果作为统一真相；
   * 2. 先求输入点到每个三角形的最近点，再在最优三角形上插值顶点法向；
   * 3. 对圆柱面、圆锥面和样条面，局部法向必须取切点附近的三角面法向，不能退回整张面的代表法向。
   *
   * @param entity 曲面面代理。
   * @param targetPoint 输入点。
   * @returns 最近点候选；无法计算时返回 `null`。
   */
  private static projectPointOnSurface(
    entity: PlaneResolvedEntity,
    targetPoint: THREE.Vector3,
  ): SurfaceProjectionCandidate | null {
    return isFeatureFace(entity)
      ? projectPointOnFeatureSurface(entity, targetPoint)
      : null;
  }

  /**
   * 读取曲面参考与平面参考。
   *
   * @param resolved 已解析实体集合。
   * @returns 成功时返回曲面与平面组合，否则返回 `null`。
   */
  private static resolveSurfacePlaneReferenceBundle(
    resolved: PlaneResolvedEntity[],
  ): SurfacePlaneReferenceBundle | null {
    const surfaceEntity = resolved.find((entity) => ReferencePlaneSolver.isNonPlanarSurfaceEntity(entity)) ?? null;
    if (!surfaceEntity) {
      return null;
    }

    const planeEntity = resolved.find((entity) => (
      entity !== surfaceEntity && ReferencePlaneSolver.readPlane(entity) !== null
    )) ?? null;
    if (!planeEntity) {
      return null;
    }

    const plane = ReferencePlaneSolver.resolvePlaneReferenceGeometry(planeEntity);
    if (!plane) {
      return null;
    }

    return {
      surfaceEntity,
      planeEntity,
      plane,
    };
  }

  /**
   * 读取面代理指定顶点的世界法向。
   *
   * @param entity 面代理对象。
   * @param normalAttribute 法向属性。
   * @param normalMatrix 法向矩阵。
   * @param index 顶点索引。
   * @returns 世界法向。
   */
  private static readSurfaceVertexWorldNormal(
    entity: FeatureFace,
    normalAttribute: THREE.BufferAttribute | null,
    normalMatrix: THREE.Matrix3,
    index: number,
  ): THREE.Vector3 {
    if (normalAttribute instanceof THREE.BufferAttribute) {
      const localNormal = ReferencePlaneSolver.readBufferVector3(normalAttribute, index);
      return localNormal.applyMatrix3(normalMatrix).normalize();
    }
    const plane = entity.getWorldPlane();
    return plane.normal.clone().normalize();
  }

  /**
   * 规范化平面法向方向。
   *
   * @param normal 原始法向。
   * @returns 规范化后的法向。
   */
  private static canonicalizePlaneNormal(normal: THREE.Vector3): THREE.Vector3 {
    const normalized = ReferencePlaneSolver.normalizeVec3(normal);
    const components: Array<number> = [normalized.x, normalized.y, normalized.z];
    for (const component of components) {
      if (Math.abs(component) <= 1e-9) {
        continue;
      }
      return component < 0 ? normalized.multiplyScalar(-1) : normalized;
    }
    return normalized;
  }

  /**
   * 把曲面上的零等值法向点加入候选面集合。
   *
   * @param candidates 候选面集合。
   * @param point 曲面上的候选点。
   * @param normal 候选点处法向。
   */
  private static pushSurfacePlanePerpendicularCandidate(
    candidates: SurfacePlanePerpendicularCandidate[],
    point: THREE.Vector3,
    normal: THREE.Vector3,
  ): void {
    if (normal.lengthSq() <= 1e-12) {
      return;
    }

    const frame = ReferencePlaneSolver.buildPlaneFrame(point, normal);
    const canonicalNormal = ReferencePlaneSolver.canonicalizePlaneNormal(frame.normal);
    const canonicalD = canonicalNormal.dot(frame.origin);
    const duplicate = candidates.some((candidate) => (
      Math.abs(candidate.canonicalNormal.dot(canonicalNormal) - 1) <= 1e-4
      && Math.abs(candidate.canonicalD - canonicalD) <= 1e-3
    ));
    if (duplicate) {
      return;
    }

    candidates.push({
      frame,
      canonicalNormal,
      canonicalD,
    });
  }

  /**
   * 把“经过指定点的曲面切平面”候选加入集合。
   *
   * @param candidates 候选面集合。
   * @param surfacePoint 曲面上的候选切点。
   * @param normal 候选切点处法向。
   * @param targetPoint 必须落在结果平面上的目标点。
   */
  private static pushSurfacePointTangentCandidate(
    candidates: SurfacePointTangentCandidate[],
    surfacePoint: THREE.Vector3,
    normal: THREE.Vector3,
    targetPoint: THREE.Vector3,
  ): void {
    if (normal.lengthSq() <= 1e-12) {
      return;
    }

    const normalizedNormal = ReferencePlaneSolver.normalizeVec3(normal);
    const signedDistance = normalizedNormal.dot(targetPoint.clone().sub(surfacePoint));
    const planePoint = targetPoint.clone().sub(normalizedNormal.clone().multiplyScalar(signedDistance));
    const frame = ReferencePlaneSolver.buildPlaneFrame(planePoint, normalizedNormal);
    const canonicalNormal = ReferencePlaneSolver.canonicalizePlaneNormal(frame.normal);
    const canonicalD = canonicalNormal.dot(frame.origin);
    const duplicate = candidates.some((candidate) => (
      Math.abs(candidate.canonicalNormal.dot(canonicalNormal) - 1) <= 1e-4
      && Math.abs(candidate.canonicalD - canonicalD) <= 1e-3
    ));
    if (duplicate) {
      return;
    }

    candidates.push({
      frame,
      surfacePoint: surfacePoint.clone(),
      canonicalNormal,
      canonicalD,
    });
  }

  /**
   * 读取边上线性插值后的根参数。
   *
   * 说明：
   * 1. 对于“平面经过目标点”条件，符号函数是二次项，而不是简单线性项；
   * 2. 这里显式求解二次方程，避免只用端点线性插值导致切平面位置严重漂移；
   * 3. 返回值会被限制在边段 `[0, 1]` 范围内。
   *
   * @param pointA 边起点。
   * @param pointB 边终点。
   * @param normalA 起点法向。
   * @param normalB 终点法向。
   * @param targetPoint 目标点。
   * @param tolerance 零等值容差。
   * @returns 边段内所有有效根参数。
   */
  private static solveSurfacePointTangentEdgeRoots(
    pointA: THREE.Vector3,
    pointB: THREE.Vector3,
    normalA: THREE.Vector3,
    normalB: THREE.Vector3,
    targetPoint: THREE.Vector3,
    tolerance: number,
  ): number[] {
    const v0 = targetPoint.clone().sub(pointA);
    const deltaPoint = pointB.clone().sub(pointA);
    const deltaNormal = normalB.clone().sub(normalA);
    const coefficient2 = -deltaNormal.dot(deltaPoint);
    const coefficient1 = deltaNormal.dot(v0) - normalA.dot(deltaPoint);
    const coefficient0 = normalA.dot(v0);

    const roots: number[] = [];
    const pushRoot = (value: number): void => {
      if (!Number.isFinite(value)) {
        return;
      }
      if (value < -tolerance || value > 1 + tolerance) {
        return;
      }
      const clamped = Math.max(0, Math.min(1, value));
      if (roots.some((candidate) => Math.abs(candidate - clamped) <= 1e-6)) {
        return;
      }
      roots.push(clamped);
    };

    if (Math.abs(coefficient2) <= tolerance) {
      if (Math.abs(coefficient1) <= tolerance) {
        if (Math.abs(coefficient0) <= tolerance) {
          pushRoot(0);
          pushRoot(1);
        }
        return roots;
      }
      pushRoot(-coefficient0 / coefficient1);
      return roots;
    }

    const discriminant = coefficient1 * coefficient1 - 4 * coefficient2 * coefficient0;
    if (discriminant < -tolerance) {
      return roots;
    }
    const safeDiscriminant = Math.max(0, discriminant);
    const sqrtDiscriminant = Math.sqrt(safeDiscriminant);
    pushRoot((-coefficient1 - sqrtDiscriminant) / (2 * coefficient2));
    pushRoot((-coefficient1 + sqrtDiscriminant) / (2 * coefficient2));
    return roots;
  }

  /**
   * 计算曲面上满足“结果平面经过指定点”的相切面候选。
   *
   * @param surfaceEntity 曲面参考实体。
   * @param targetPoint 必须位于结果平面上的点。
   * @returns 候选平面集合。
   */
  private static collectSurfacePointTangentCandidates(
    surfaceEntity: PlaneResolvedEntity,
    targetPoint: THREE.Vector3,
  ): SurfacePointTangentCandidate[] {
    if (!isFeatureFace(surfaceEntity)) {
      return [];
    }

    const positionAttribute = surfaceEntity.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
      return [];
    }

    surfaceEntity.updateWorldMatrix(true, false);
    const indexAttribute = surfaceEntity.geometry.getIndex();
    const vertexCount = indexAttribute instanceof THREE.BufferAttribute
      ? indexAttribute.count
      : positionAttribute.count;
    if (vertexCount < 3) {
      return [];
    }

    const normalAttribute = surfaceEntity.geometry.getAttribute('normal');
    const worldNormalMatrix = new THREE.Matrix3().getNormalMatrix(surfaceEntity.matrixWorld);
    const zeroTolerance = 1e-4;
    const candidates: SurfacePointTangentCandidate[] = [];

    const pushRootsOnEdge = (
      pointA: THREE.Vector3,
      pointB: THREE.Vector3,
      normalA: THREE.Vector3,
      normalB: THREE.Vector3,
    ): void => {
      const roots = ReferencePlaneSolver.solveSurfacePointTangentEdgeRoots(
        pointA,
        pointB,
        normalA,
        normalB,
        targetPoint,
        zeroTolerance,
      );
      roots.forEach((root) => {
        const point = pointA.clone().lerp(pointB, root);
        const normal = normalA.clone().lerp(normalB, root);
        if (normal.lengthSq() <= 1e-12) {
          return;
        }
        ReferencePlaneSolver.pushSurfacePointTangentCandidate(candidates, point, normal, targetPoint);
      });
    };

    for (let cursor = 0; cursor + 2 < vertexCount; cursor += 3) {
      const indexA = indexAttribute instanceof THREE.BufferAttribute ? Number(indexAttribute.getX(cursor)) : cursor;
      const indexB = indexAttribute instanceof THREE.BufferAttribute ? Number(indexAttribute.getX(cursor + 1)) : cursor + 1;
      const indexC = indexAttribute instanceof THREE.BufferAttribute ? Number(indexAttribute.getX(cursor + 2)) : cursor + 2;

      const pointA = ReferencePlaneSolver.readSurfaceVertexWorldPosition(surfaceEntity, positionAttribute, indexA);
      const pointB = ReferencePlaneSolver.readSurfaceVertexWorldPosition(surfaceEntity, positionAttribute, indexB);
      const pointC = ReferencePlaneSolver.readSurfaceVertexWorldPosition(surfaceEntity, positionAttribute, indexC);
      const pointNormalAttribute = normalAttribute instanceof THREE.BufferAttribute ? normalAttribute : null;
      const normalA = ReferencePlaneSolver.readSurfaceVertexWorldNormal(surfaceEntity, pointNormalAttribute, worldNormalMatrix, indexA);
      const normalB = ReferencePlaneSolver.readSurfaceVertexWorldNormal(surfaceEntity, pointNormalAttribute, worldNormalMatrix, indexB);
      const normalC = ReferencePlaneSolver.readSurfaceVertexWorldNormal(surfaceEntity, pointNormalAttribute, worldNormalMatrix, indexC);
      const signedA = normalA.dot(targetPoint.clone().sub(pointA));
      const signedB = normalB.dot(targetPoint.clone().sub(pointB));
      const signedC = normalC.dot(targetPoint.clone().sub(pointC));

      if (Math.abs(signedA) <= zeroTolerance) {
        ReferencePlaneSolver.pushSurfacePointTangentCandidate(candidates, pointA, normalA, targetPoint);
      }
      if (Math.abs(signedB) <= zeroTolerance) {
        ReferencePlaneSolver.pushSurfacePointTangentCandidate(candidates, pointB, normalB, targetPoint);
      }
      if (Math.abs(signedC) <= zeroTolerance) {
        ReferencePlaneSolver.pushSurfacePointTangentCandidate(candidates, pointC, normalC, targetPoint);
      }

      pushRootsOnEdge(pointA, pointB, normalA, normalB);
      pushRootsOnEdge(pointB, pointC, normalB, normalC);
      pushRootsOnEdge(pointC, pointA, normalC, normalA);

      const centroidPoint = pointA.clone().add(pointB).add(pointC).multiplyScalar(1 / 3);
      const centroidNormal = normalA.clone().add(normalB).add(normalC).multiplyScalar(1 / 3);
      if (centroidNormal.lengthSq() > 1e-12) {
        const centroidSigned = ReferencePlaneSolver.normalizeVec3(centroidNormal).dot(targetPoint.clone().sub(centroidPoint));
        const sameVertexSign = (
          signedA * signedB > 0
          && signedB * signedC > 0
          && signedA * signedC > 0
        );
        const centroidBreaksUniformSign = (
          Math.abs(centroidSigned) <= zeroTolerance
          || signedA * centroidSigned < 0
          || signedB * centroidSigned < 0
          || signedC * centroidSigned < 0
        );
        if (sameVertexSign && centroidBreaksUniformSign) {
          pushRootsOnEdge(pointA, centroidPoint, normalA, ReferencePlaneSolver.normalizeVec3(centroidNormal));
          pushRootsOnEdge(pointB, centroidPoint, normalB, ReferencePlaneSolver.normalizeVec3(centroidNormal));
          pushRootsOnEdge(pointC, centroidPoint, normalC, ReferencePlaneSolver.normalizeVec3(centroidNormal));
        }
      }
    }

    return candidates.sort((left, right) => {
        if (Math.abs(left.frame.normal.z - right.frame.normal.z) > 1e-6) {
          return right.frame.normal.z - left.frame.normal.z;
        }
        if (Math.abs(left.frame.normal.y - right.frame.normal.y) > 1e-6) {
          return right.frame.normal.y - left.frame.normal.y;
        }
        if (Math.abs(left.frame.normal.x - right.frame.normal.x) > 1e-6) {
          return right.frame.normal.x - left.frame.normal.x;
        }
        if (Math.abs(left.frame.origin.z - right.frame.origin.z) > 1e-6) {
          return right.frame.origin.z - left.frame.origin.z;
        }
        if (Math.abs(left.frame.origin.y - right.frame.origin.y) > 1e-6) {
          return right.frame.origin.y - left.frame.origin.y;
        }
        if (Math.abs(left.frame.origin.x - right.frame.origin.x) > 1e-6) {
          return right.frame.origin.x - left.frame.origin.x;
        }
        return 0;
      });
  }

  /**
   * 解析曲面点相切场景的默认优先方向。
   *
   * @returns 优先方向；无法读取时回退世界 Y 轴。
   */
  private resolvePreferredSurfacePointTangentDirection(): THREE.Vector3 {
    const cameraUpDirection = this.controller.getCurrentCameraUpDirection();
    if (cameraUpDirection && cameraUpDirection.lengthSq() > 1e-12) {
      return cameraUpDirection.clone().normalize();
    }
    return new THREE.Vector3(0, 1, 0);
  }

  /**
   * 依据视图向上方向排序“曲面 + 点相切”候选。
   *
   * @param candidates 候选集合。
   * @returns 排序后的候选集合。
   */
  private orderSurfacePointTangentCandidates(
    candidates: SurfacePointTangentCandidate[],
  ): SurfacePointTangentCandidate[] {
    const preferredDirection = this.resolvePreferredSurfacePointTangentDirection();
    const validCandidates = candidates.filter((candidate) => (
      candidate.surfacePoint instanceof THREE.Vector3
      && candidate.frame.normal instanceof THREE.Vector3
    ));

    return validCandidates.sort((left, right) => {
      const pointScoreDelta = right.surfacePoint.dot(preferredDirection) - left.surfacePoint.dot(preferredDirection);
      if (Math.abs(pointScoreDelta) > 1e-6) {
        return pointScoreDelta;
      }

      const normalScoreDelta = right.frame.normal.dot(preferredDirection) - left.frame.normal.dot(preferredDirection);
      if (Math.abs(normalScoreDelta) > 1e-6) {
        return normalScoreDelta;
      }

      return 0;
    });
  }

  /**
   * 计算曲面上满足“局部法向与目标平面法向正交”的相切面候选。
   *
   * @param surfaceEntity 曲面参考实体。
   * @param planeNormal 目标平面法向。
   * @param reverseOffset 是否翻转默认侧别。
   * @returns 候选平面集合。
   */
  private static collectSurfacePlanePerpendicularCandidates(
    surfaceEntity: PlaneResolvedEntity,
    planeNormal: THREE.Vector3,
  ): PlaneFrame[] {
    if (!isFeatureFace(surfaceEntity)) {
      return [];
    }

    const positionAttribute = surfaceEntity.geometry.getAttribute('position');
    if (!(positionAttribute instanceof THREE.BufferAttribute)) {
      return [];
    }

    surfaceEntity.updateWorldMatrix(true, false);
    const indexAttribute = surfaceEntity.geometry.getIndex();
    const vertexCount = indexAttribute instanceof THREE.BufferAttribute
      ? indexAttribute.count
      : positionAttribute.count;
    if (vertexCount < 3) {
      return [];
    }

    const normalAttribute = surfaceEntity.geometry.getAttribute('normal');
    const worldNormalMatrix = new THREE.Matrix3().getNormalMatrix(surfaceEntity.matrixWorld);
    const orthogonalTolerance = 1e-4;
    const candidates: SurfacePlanePerpendicularCandidate[] = [];

    const pushEdgeZeroCrossing = (
      pointA: THREE.Vector3,
      pointB: THREE.Vector3,
      normalA: THREE.Vector3,
      normalB: THREE.Vector3,
      signedA: number,
      signedB: number,
    ): void => {
      const isAZero = Math.abs(signedA) <= orthogonalTolerance;
      const isBZero = Math.abs(signedB) <= orthogonalTolerance;
      if (isAZero) {
        ReferencePlaneSolver.pushSurfacePlanePerpendicularCandidate(candidates, pointA, normalA);
      }
      if (isBZero) {
        ReferencePlaneSolver.pushSurfacePlanePerpendicularCandidate(candidates, pointB, normalB);
      }
      if (isAZero || isBZero) {
        return;
      }
      if (signedA * signedB > 0) {
        return;
      }

      const lerpT = signedA / (signedA - signedB);
      const point = pointA.clone().lerp(pointB, lerpT);
      const normal = normalA.clone().lerp(normalB, lerpT).normalize();
      ReferencePlaneSolver.pushSurfacePlanePerpendicularCandidate(candidates, point, normal);
    };

    for (let cursor = 0; cursor + 2 < vertexCount; cursor += 3) {
      const indexA = indexAttribute instanceof THREE.BufferAttribute ? Number(indexAttribute.getX(cursor)) : cursor;
      const indexB = indexAttribute instanceof THREE.BufferAttribute ? Number(indexAttribute.getX(cursor + 1)) : cursor + 1;
      const indexC = indexAttribute instanceof THREE.BufferAttribute ? Number(indexAttribute.getX(cursor + 2)) : cursor + 2;

      const pointA = ReferencePlaneSolver.readSurfaceVertexWorldPosition(surfaceEntity, positionAttribute, indexA);
      const pointB = ReferencePlaneSolver.readSurfaceVertexWorldPosition(surfaceEntity, positionAttribute, indexB);
      const pointC = ReferencePlaneSolver.readSurfaceVertexWorldPosition(surfaceEntity, positionAttribute, indexC);
      const pointNormalAttribute = normalAttribute instanceof THREE.BufferAttribute ? normalAttribute : null;
      const normalA = ReferencePlaneSolver.readSurfaceVertexWorldNormal(surfaceEntity, pointNormalAttribute, worldNormalMatrix, indexA);
      const normalB = ReferencePlaneSolver.readSurfaceVertexWorldNormal(surfaceEntity, pointNormalAttribute, worldNormalMatrix, indexB);
      const normalC = ReferencePlaneSolver.readSurfaceVertexWorldNormal(surfaceEntity, pointNormalAttribute, worldNormalMatrix, indexC);
      const signedA = normalA.dot(planeNormal);
      const signedB = normalB.dot(planeNormal);
      const signedC = normalC.dot(planeNormal);

      if (
        Math.abs(signedA) <= orthogonalTolerance
        && Math.abs(signedB) <= orthogonalTolerance
        && Math.abs(signedC) <= orthogonalTolerance
      ) {
        const centroidPoint = pointA.clone().add(pointB).add(pointC).multiplyScalar(1 / 3);
        const centroidNormal = normalA.clone().add(normalB).add(normalC).multiplyScalar(1 / 3).normalize();
        ReferencePlaneSolver.pushSurfacePlanePerpendicularCandidate(candidates, centroidPoint, centroidNormal);
        continue;
      }

      pushEdgeZeroCrossing(pointA, pointB, normalA, normalB, signedA, signedB);
      pushEdgeZeroCrossing(pointB, pointC, normalB, normalC, signedB, signedC);
      pushEdgeZeroCrossing(pointC, pointA, normalC, normalA, signedC, signedA);
    }

    return candidates
      .map((candidate) => candidate.frame)
      .sort((left, right) => {
        if (Math.abs(left.normal.z - right.normal.z) > 1e-6) {
          return right.normal.z - left.normal.z;
        }
        if (Math.abs(left.normal.y - right.normal.y) > 1e-6) {
          return right.normal.y - left.normal.y;
        }
        if (Math.abs(left.normal.x - right.normal.x) > 1e-6) {
          return right.normal.x - left.normal.x;
        }
        if (Math.abs(left.origin.z - right.origin.z) > 1e-6) {
          return right.origin.z - left.origin.z;
        }
        if (Math.abs(left.origin.y - right.origin.y) > 1e-6) {
          return right.origin.y - left.origin.y;
        }
        if (Math.abs(left.origin.x - right.origin.x) > 1e-6) {
          return right.origin.x - left.origin.x;
        }
        return 0;
      });
  }

  /**
   * 依据法线方向选择曲面相切且垂直于平面的默认解。
   *
   * @param frameCandidates 候选平面集合。
   * @param reverseOffset 是否翻转默认侧别。
   * @param preferredNormalDirection
   * @returns 选中的唯一候选；不存在时返回 `null`。
   */
  private static selectSurfacePlanePerpendicularCandidate(
    frameCandidates: PlaneFrame[],
    reverseOffset: boolean,
    preferredNormalDirection: THREE.Vector3 | null,
  ): PlaneFrame | null {
    if (frameCandidates.length === 0) {
      return null;
    }
    if (frameCandidates.length === 1) {
      return frameCandidates[0];
    }

    if (!preferredNormalDirection || preferredNormalDirection.lengthSq() <= 1e-12) {
      return reverseOffset ? frameCandidates[frameCandidates.length - 1] : frameCandidates[0];
    }

    let selectedFrame = frameCandidates[0];
    let selectedScore = selectedFrame.normal.dot(preferredNormalDirection);
    frameCandidates.slice(1).forEach((frame) => {
      const score = frame.normal.dot(preferredNormalDirection);
      const shouldReplace = reverseOffset ? score < selectedScore : score > selectedScore;
      if (shouldReplace) {
        selectedFrame = frame;
        selectedScore = score;
      }
    });
    return selectedFrame;
  }

  /**
   * 计算当前视图下更偏好的垂直法向方向。
   *
   * @param planeNormal 参考平面法向。
   * @returns 偏好方向；无法计算时返回 `null`。
   */
  private resolvePreferredPerpendicularNormalDirection(
    planeNormal: THREE.Vector3,
  ): THREE.Vector3 | null {
    const normalizedPlaneNormal = ReferencePlaneSolver.normalizeVec3(planeNormal);
    const projectDirectionToPerpendicularSpace = (direction: THREE.Vector3 | null): THREE.Vector3 | null => {
      if (!direction || direction.lengthSq() <= 1e-12) {
        return null;
      }
      const projected = direction.clone()
        .sub(normalizedPlaneNormal.clone().multiplyScalar(direction.dot(normalizedPlaneNormal)));
      if (projected.lengthSq() <= 1e-12) {
        return null;
      }
      return projected.normalize();
    };

    const resolveViewLateralDirection = (): THREE.Vector3 | null => {
      const currentViewDirection = this.controller.getCurrentViewDirection();
      if (!currentViewDirection || currentViewDirection.lengthSq() <= 1e-12) {
        return null;
      }

      const viewToUserDirection = currentViewDirection.clone().multiplyScalar(-1).normalize();
      const lateralDirection = normalizedPlaneNormal.clone().cross(viewToUserDirection);
      if (lateralDirection.lengthSq() <= 1e-12) {
        return null;
      }
      return lateralDirection.normalize();
    };

    const preferredDirections: Array<THREE.Vector3 | null> = [
      resolveViewLateralDirection(),
      projectDirectionToPerpendicularSpace(this.controller.getCurrentCameraUpDirection()),
      projectDirectionToPerpendicularSpace(this.controller.getCurrentViewDirection()?.clone().multiplyScalar(-1) ?? null),
      projectDirectionToPerpendicularSpace(new THREE.Vector3(0, 0, 1)),
      projectDirectionToPerpendicularSpace(new THREE.Vector3(0, 1, 0)),
      projectDirectionToPerpendicularSpace(new THREE.Vector3(1, 0, 0)),
    ];

    return preferredDirections.find((direction) => direction !== null) ?? null;
  }

  /**
   * 构造失败结果。
   *
   * @param status 求解状态。
   * @param message 状态描述。
   * @param diagnostics 可选诊断列表。
   * @returns 失败结果。
   */
  private static buildErrorResult(
    status: PlaneSolveStatus,
    message: string,
    diagnostics: PlaneDiagnostic[] = []
  ): PlaneFeatureResult {
    // 未完全定义使用 warning，其余失败使用 error。
    const severity = status === PlaneSolveStatus.UnderDefined ? ReferenceDiagnosticSeverity.Warning : ReferenceDiagnosticSeverity.Error;
    return {
      solveStatus: status,
      statusText: message,
      uniqueSolution: false,
      solutionCount: 0,
      activeSolutionIndex: 0,
      planes: [],
      editableFields: [],
      availableConstraints: [
        PlaneConstraintType.Distance,
        PlaneConstraintType.Parallel,
        PlaneConstraintType.Perpendicular,
        PlaneConstraintType.Normal,
        PlaneConstraintType.Coincident,
        PlaneConstraintType.Angle,
        PlaneConstraintType.MidPlane,
        PlaneConstraintType.Tangent,
        PlaneConstraintType.OnSurface,
        PlaneConstraintType.Project,
      ],
      availableProjectionTypes: [PlaneProjectionType.NearestOnSurface, PlaneProjectionType.AlongSketchNormal],
      diagnostics: diagnostics.length > 0
        ? diagnostics
        : [{ code: 'PLANE_SOLVE_FAILED', message, severity }],
    };
  }

  /**
   * 构造成功结果。
   *
   * @param params 成功结果参数。
   * @returns 成功结果。
   */
  private static buildSuccessResult(params: {
    frameCandidates: PlaneFrame[];
    statusText: string;
    editableFields?: PlaneEditableField[];
    availableConstraints?: PlaneConstraintType[];
    availableProjectionTypes?: PlaneProjectionType[];
    diagnostics?: PlaneDiagnostic[];
    activeSolutionIndex?: number;
  }): PlaneFeatureResult {
    // 基于候选平面数量推断唯一解与活动索引。
    const solutionCount = params.frameCandidates.length;
    const uniqueSolution = solutionCount === 1;
    const activeSolutionIndex = Math.max(0, Math.min(solutionCount - 1, params.activeSolutionIndex ?? 0));
    return {
      solveStatus: PlaneSolveStatus.FullyDefined,
      statusText: params.statusText,
      uniqueSolution,
      solutionCount,
      activeSolutionIndex,
      planes: params.frameCandidates,
      editableFields: params.editableFields ?? [],
      availableConstraints: params.availableConstraints ?? [
        PlaneConstraintType.Distance,
        PlaneConstraintType.Parallel,
        PlaneConstraintType.Perpendicular,
        PlaneConstraintType.Normal,
        PlaneConstraintType.Coincident,
        PlaneConstraintType.Angle,
        PlaneConstraintType.MidPlane,
        PlaneConstraintType.Tangent,
        PlaneConstraintType.OnSurface,
        PlaneConstraintType.Project,
      ],
      availableProjectionTypes: params.availableProjectionTypes ?? [PlaneProjectionType.NearestOnSurface, PlaneProjectionType.AlongSketchNormal],
      diagnostics: params.diagnostics ?? [],
    };
  }

  /**
   * 求解线重合约束。
   *
   * @param resolved 已解析实体。
   * @returns 可处理时返回结果，否则返回 `null`。
   */
  private static solveLineCoincidentConstraint(resolved: PlaneResolvedEntity[]): PlaneFeatureResult | null {
    const firstLine = ReferencePlaneSolver.resolveLineReferenceGeometry(resolved[0]);
    if (!firstLine) {
      return null;
    }
    const second = resolved[1];
    if (!second) {
      return ReferencePlaneSolver.buildLineUnderDefinedResult('线重合建面需要补充第二参考（点或线）');
    }
    const secondLine = ReferencePlaneSolver.resolveLineReferenceGeometry(second);
    if (secondLine) {
      return ReferencePlaneSolver.solveCoincidentByTwoLines(firstLine, secondLine);
    }
    const secondPoint = ReferencePlaneSolver.readPoint(second);
    if (secondPoint) {
      return ReferencePlaneSolver.solveCoincidentByLineAndPoint(firstLine, secondPoint);
    }
    return ReferencePlaneSolver.buildLineInconsistentResult('线重合建面仅支持“线/点”作为第二参考');
  }

  /**
   * 求解线垂直约束。
   *
   * @param resolved 已解析实体。
   * @returns 可处理时返回结果，否则返回 `null`。
   */
  private static solveLinePerpendicularConstraint(resolved: PlaneResolvedEntity[]): PlaneFeatureResult | null {
    const firstLine = ReferencePlaneSolver.resolveLineReferenceGeometry(resolved[0]);
    if (!firstLine) {
      return null;
    }
    const second = resolved[1];
    if (!second) {
      return ReferencePlaneSolver.buildLineUnderDefinedResult('线垂直建面需要第二参考用于锚定平面位置');
    }
    const secondPlane = ReferencePlaneSolver.resolvePlaneReferenceGeometry(second);
    if (secondPlane) {
      return ReferencePlaneSolver.solvePerpendicularByLineAndPlane(firstLine, secondPlane);
    }
    const anchorPoint = ReferencePlaneSolver.resolveSecondAnchorPoint(second);
    if (!anchorPoint) {
      return ReferencePlaneSolver.buildLineInconsistentResult('线垂直建面的第二参考不可解析为有效锚点');
    }
    return ReferencePlaneSolver.buildSuccessResult({
      frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(anchorPoint, firstLine.direction)],
      statusText: '完全定义',
      availableConstraints: LINE_REFERENCE_AVAILABLE_CONSTRAINTS,
      diagnostics: [{
        code: 'PLANE_LINE_PERPENDICULAR_READY',
        message: '已按第一线方向构造垂直平面并通过第二参考锚点',
        severity: ReferenceDiagnosticSeverity.Info,
      }],
    });
  }

  /**
   * 求解线投影约束。
   *
   * @param resolved 已解析实体。
   * @returns 可处理时返回结果，否则返回 `null`。
   */
  private static solveLineProjectConstraint(resolved: PlaneResolvedEntity[]): PlaneFeatureResult | null {
    const firstLine = ReferencePlaneSolver.resolveLineReferenceGeometry(resolved[0]);
    if (!firstLine) {
      return null;
    }
    const second = resolved[1];
    if (!second) {
      return ReferencePlaneSolver.buildLineUnderDefinedResult('线投影建面需要第二参考用于确定投影分支');
    }
    const secondLine = ReferencePlaneSolver.resolveLineReferenceGeometry(second);
    if (secondLine) {
      let normalCandidate = firstLine.direction.clone().cross(secondLine.direction);
      if (normalCandidate.length() <= LINEAR_EPSILON) {
        normalCandidate = firstLine.direction.clone().cross(secondLine.point.clone().sub(firstLine.point));
      }
      if (normalCandidate.length() <= LINEAR_EPSILON) {
        return ReferencePlaneSolver.buildLineUnderDefinedResult('两条线无法提供稳定投影法向，请调整第二参考');
      }
      return ReferencePlaneSolver.buildSuccessResult({
        frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(firstLine.point, ReferencePlaneSolver.normalizeVec3(normalCandidate))],
        statusText: '完全定义',
        availableConstraints: LINE_REFERENCE_AVAILABLE_CONSTRAINTS,
        diagnostics: [{
          code: 'PLANE_LINE_PROJECT_READY',
          message: '已按“线+线投影”构造基准面',
          severity: ReferenceDiagnosticSeverity.Info,
        }],
      });
    }
    const secondPoint = ReferencePlaneSolver.readPoint(second);
    if (!secondPoint) {
      return ReferencePlaneSolver.buildLineInconsistentResult('线投影建面仅支持“线/点”作为第二参考');
    }
    const pointNormalCandidate = firstLine.direction.clone().cross(secondPoint.clone().sub(firstLine.point));
    if (pointNormalCandidate.length() <= LINEAR_EPSILON) {
      return ReferencePlaneSolver.buildLineUnderDefinedResult('点位于第一线方向上，无法确定唯一投影平面');
    }
    return ReferencePlaneSolver.buildSuccessResult({
      frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(firstLine.point, ReferencePlaneSolver.normalizeVec3(pointNormalCandidate))],
      statusText: '完全定义',
      availableConstraints: LINE_REFERENCE_AVAILABLE_CONSTRAINTS,
      diagnostics: [{
        code: 'PLANE_LINE_POINT_PROJECT_READY',
        message: '已按“线+点投影”构造基准面',
        severity: ReferenceDiagnosticSeverity.Info,
      }],
    });
  }

  /**
   * 解析线参考几何。
   *
   * @param entity 已解析实体。
   * @returns 线参考几何；无法解析时返回 `null`。
   */
  private static resolveLineReferenceGeometry(entity: PlaneResolvedEntity | undefined): LineReferenceGeometry | null {
    if (!entity) {
      return null;
    }
    const direction = ReferencePlaneSolver.readDirection(entity);
    if (!direction) {
      return null;
    }
    const endpoints = ReferencePlaneSolver.readLineEndpoints(entity);
    return {
      point: endpoints
        ? endpoints.start.clone().add(endpoints.end).multiplyScalar(0.5)
        : (ReferencePlaneSolver.readPoint(entity) ?? new THREE.Vector3(0, 0, 0)),
      direction: ReferencePlaneSolver.normalizeVec3(direction),
      startPoint: endpoints?.start.clone(),
      endPoint: endpoints?.end.clone(),
    };
  }

  /**
   * 解析平面参考几何。
   *
   * @param entity 已解析实体。
   * @returns 平面参考几何；无法解析时返回 `null`。
   */
  private static resolvePlaneReferenceGeometry(entity: PlaneResolvedEntity | undefined): PlaneReferenceGeometry | null {
    if (!entity) {
      return null;
    }
    const plane = ReferencePlaneSolver.readPlane(entity);
    if (!plane) {
      return null;
    }
    return { point: plane.point, normal: plane.normal };
  }

  /**
   * 求解线加平面的垂直建面。
   *
   * @param firstLine 第一线参考。
   * @param secondPlane 第二平面参考。
   * @returns 求解结果。
   */
  private static solvePerpendicularByLineAndPlane(firstLine: LineReferenceGeometry, secondPlane: PlaneReferenceGeometry): PlaneFeatureResult {
    const directionDotPlaneNormal = Math.abs(firstLine.direction.dot(secondPlane.normal));
    const linePointToPlaneDistance = Math.abs(firstLine.point.clone().sub(secondPlane.point).dot(secondPlane.normal));
    if (directionDotPlaneNormal > LINEAR_EPSILON || linePointToPlaneDistance > LINEAR_EPSILON) {
      return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.NoSolution, '线不属于所选平面，无法生成“与该面垂直且共用该线”的基准面');
    }
    const targetNormalCandidate = firstLine.direction.clone().cross(secondPlane.normal);
    if (targetNormalCandidate.length() <= LINEAR_EPSILON) {
      return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.Inconsistent, '线与面的几何关系退化，无法稳定求解垂直基准面');
    }
    return ReferencePlaneSolver.buildSuccessResult({
      frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(firstLine.point, ReferencePlaneSolver.normalizeVec3(targetNormalCandidate))],
      statusText: '完全定义',
      availableConstraints: LINE_REFERENCE_AVAILABLE_CONSTRAINTS,
      diagnostics: [{
        code: 'PLANE_LINE_PLANE_PERPENDICULAR_READY',
        message: '已按“线+面垂直”构造基准面，且与所选面共用该线',
        severity: ReferenceDiagnosticSeverity.Info,
      }],
    });
  }

  /**
   * 解析第二参考锚点。
   *
   * @param second 第二参考实体。
   * @returns 锚点；无法解析时返回 `null`。
   */
  private static resolveSecondAnchorPoint(second: PlaneResolvedEntity | undefined): THREE.Vector3 | null {
    if (!second) {
      return null;
    }
    const point = ReferencePlaneSolver.readPoint(second);
    if (point) {
      return point;
    }
    return ReferencePlaneSolver.resolveLineReferenceGeometry(second)?.point ?? null;
  }

  /**
   * 构造线参考欠定义结果。
   *
   * @param message 错误消息。
   * @returns 欠定义结果。
   */
  private static buildLineUnderDefinedResult(message: string): PlaneFeatureResult {
    return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.UnderDefined, message);
  }

  /**
   * 构造线参考不一致结果。
   *
   * @param message 错误消息。
   * @returns 不一致结果。
   */
  private static buildLineInconsistentResult(message: string): PlaneFeatureResult {
    return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.Inconsistent, message);
  }

  /**
   * 求解线加点的重合建面。
   *
   * @param line 线参考。
   * @param point 点参考。
   * @returns 求解结果。
   */
  private static solveCoincidentByLineAndPoint(line: LineReferenceGeometry, point: THREE.Vector3): PlaneFeatureResult {
    const normalCandidate = line.direction.clone().cross(point.clone().sub(line.point));
    if (normalCandidate.length() <= LINEAR_EPSILON) {
      return ReferencePlaneSolver.buildLineUnderDefinedResult('点位于线参考上，重合平面存在多个候选解');
    }
    return ReferencePlaneSolver.buildSuccessResult({
      frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(line.point, ReferencePlaneSolver.normalizeVec3(normalCandidate))],
      statusText: '完全定义',
      availableConstraints: LINE_REFERENCE_AVAILABLE_CONSTRAINTS,
      diagnostics: [{
        code: 'PLANE_LINE_POINT_COINCIDENT_READY',
        message: '已按“线+点重合”构造唯一基准面',
        severity: ReferenceDiagnosticSeverity.Info,
      }],
    });
  }

  /**
   * 求解两条线的重合建面。
   *
   * @param first 第一线参考。
   * @param second 第二线参考。
   * @returns 求解结果。
   */
  private static solveCoincidentByTwoLines(first: LineReferenceGeometry, second: LineReferenceGeometry): PlaneFeatureResult {
    const lineDelta = second.point.clone().sub(first.point);
    const directionCross = first.direction.clone().cross(second.direction);
    const directionCrossLength = directionCross.length();
    if (directionCrossLength <= LINEAR_EPSILON) {
      const parallelNormalCandidate = first.direction.clone().cross(lineDelta);
      if (parallelNormalCandidate.length() <= LINEAR_EPSILON) {
        return ReferencePlaneSolver.buildLineUnderDefinedResult('两条线共线，重合平面存在多个候选解');
      }
      return ReferencePlaneSolver.buildSuccessResult({
        frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(first.point, ReferencePlaneSolver.normalizeVec3(parallelNormalCandidate))],
        statusText: '完全定义',
        availableConstraints: LINE_REFERENCE_AVAILABLE_CONSTRAINTS,
        diagnostics: [{
          code: 'PLANE_TWO_LINE_COINCIDENT_PARALLEL_READY',
          message: '两条平行线已成功构造重合平面',
          severity: ReferenceDiagnosticSeverity.Info,
        }],
      });
    }
    const normal = ReferencePlaneSolver.normalizeVec3(directionCross);
    if (Math.abs(lineDelta.dot(normal)) > LINEAR_EPSILON) {
      return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.NoSolution, '两条线异面，无法构造同时重合的基准面');
    }
    return ReferencePlaneSolver.buildSuccessResult({
      frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(first.point, normal)],
      statusText: '完全定义',
      availableConstraints: LINE_REFERENCE_AVAILABLE_CONSTRAINTS,
      diagnostics: [{
        code: 'PLANE_TWO_LINE_COINCIDENT_READY',
        message: '两条线已成功构造重合平面',
        severity: ReferenceDiagnosticSeverity.Info,
      }],
    });
  }

  /**
   * 求解重合约束。
   *
   * @param resolved 已解析实体。
   * @returns 求解结果。
   */
  private static solveCoincident(resolved: PlaneResolvedEntity[]): PlaneFeatureResult {
    const first = resolved[0];
    if (!first) {
      return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.UnderDefined, '重合建面至少需要一个参考');
    }
    const lineConstraintResult = ReferencePlaneSolver.solveLineCoincidentConstraint(resolved);
    if (lineConstraintResult) {
      return lineConstraintResult;
    }
    const plane = ReferencePlaneSolver.readPlane(first);
    if (plane) {
      return ReferencePlaneSolver.buildSuccessResult({
        frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(plane.point, plane.normal)],
        statusText: '完全定义',
        availableConstraints: [PlaneConstraintType.Coincident, PlaneConstraintType.Distance, PlaneConstraintType.Parallel],
      });
    }
    const point = ReferencePlaneSolver.readPoint(first);
    if (point) {
      const uniquePoints = ReferencePlaneSolver.collectUniquePoints(resolved);
      const threePointResult = ReferencePlaneSolver.solveCoincidentByThreePoints(uniquePoints);
      if (threePointResult) {
        return threePointResult;
      }
      return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.UnderDefined, '仅有点参考时平面仍未完全定义');
    }
    return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.Inconsistent, '参考实体无法用于重合建面');
  }

  /**
   * 求解角度约束。
   *
   * @param resolved 已解析实体。
   * @param angleDeg 角度值。
   * @param options 附加选项。
   * @returns 求解结果。
   */
  private static solveAngle(
    resolved: PlaneResolvedEntity[],
    angleDeg: number,
    options: SolveAngleOptions = {}
  ): PlaneFeatureResult {
    const referenceBundle = ReferencePlaneSolver.resolveAngleReferenceBundle(resolved);
    if (!referenceBundle) {
      return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.Inconsistent, '参考类型不满足角度建面要求');
    }
    const axisPoint = ReferencePlaneSolver.resolveAxisPoint(referenceBundle.lineEntity, referenceBundle.basePlane.point);
    if (options.preservePerpendicularState) {
      // 线面混合角度场景优先沿垂直基准解旋转，确保 90 度附近的行为稳定。
      const preserved = ReferencePlaneSolver.solveAngleByPerpendicularBaseline(referenceBundle, axisPoint, angleDeg);
      if (preserved) {
        return preserved;
      }
    }
    const angleRad = Number.isFinite(angleDeg) ? (angleDeg * Math.PI) / 180 : 0;
    const rotatedNormal = ReferencePlaneSolver.rotateAroundAxis(referenceBundle.basePlane.normal, referenceBundle.axisDirection, angleRad);
    const origin = ReferencePlaneSolver.projectPointOnLine(referenceBundle.basePlane.point, axisPoint, referenceBundle.axisDirection);
    return ReferencePlaneSolver.buildSuccessResult({
      frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(origin, rotatedNormal)],
      statusText: '完全定义',
      editableFields: [PlaneEditableField.Angle],
      availableConstraints: [PlaneConstraintType.Angle, PlaneConstraintType.Coincident],
    });
  }

  /**
   * 判断两点是否可视为同一点。
   *
   * @param a 点 A。
   * @param b 点 B。
   * @returns 是否重合。
   */
  private static isSamePoint(a: THREE.Vector3, b: THREE.Vector3): boolean {
    return a.distanceToSquared(b) <= POINT_MERGE_EPSILON ** 2;
  }

  /**
   * 收集去重后的点集合。
   *
   * @param resolved 已解析实体。
   * @returns 去重点集合。
   */
  private static collectUniquePoints(resolved: PlaneResolvedEntity[]): THREE.Vector3[] {
    const points: THREE.Vector3[] = [];
    resolved.forEach((entity) => {
      const point = ReferencePlaneSolver.readPoint(entity);
      if (!point) {
        return;
      }
      if (!points.some((item) => ReferencePlaneSolver.isSamePoint(item, point))) {
        points.push(point);
      }
    });
    return points;
  }

  /**
   * 通过三点求解重合平面。
   *
   * @param points 点集合。
   * @returns 可求解时返回结果，否则返回 `null`。
   */
  private static solveCoincidentByThreePoints(points: THREE.Vector3[]): PlaneFeatureResult | null {
    if (points.length < 3) {
      return null;
    }
    const anchor = points[0];
    for (let i = 1; i < points.length - 1; i += 1) {
      for (let j = i + 1; j < points.length; j += 1) {
        const normal = points[i].clone().sub(anchor).cross(points[j].clone().sub(anchor));
        if (normal.length() <= THREE_POINT_NORMAL_EPSILON) {
          continue;
        }
        const origin = anchor.clone().add(points[i]).add(points[j]).multiplyScalar(1 / 3);
        return ReferencePlaneSolver.buildSuccessResult({
          frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(origin, normal)],
          statusText: '完全定义（通过三点）',
          availableConstraints: [PlaneConstraintType.Coincident, PlaneConstraintType.Project, PlaneConstraintType.Parallel],
        });
      }
    }
    return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.UnderDefined, '三点共线，基准面未完全定义');
  }

  /**
   * 解析旋转轴上的锚点。
   *
   * @param lineEntity 线性实体。
   * @param fallbackPoint 兜底点。
   * @returns 轴点。
   */
  private static resolveAxisPoint(lineEntity: PlaneResolvedEntity, fallbackPoint: THREE.Vector3): THREE.Vector3 {
    const endpoints = ReferencePlaneSolver.readLineEndpoints(lineEntity);
    if (endpoints) {
      return endpoints.start.clone().add(endpoints.end).multiplyScalar(0.5);
    }
    return ReferencePlaneSolver.readPoint(lineEntity) ?? fallbackPoint.clone();
  }

  /**
   * 绕给定轴旋转点。
   *
   * @param point 待旋转点。
   * @param axisPoint 轴上一点。
   * @param axisDirection 轴方向。
   * @param angleRad 弧度角。
   * @returns 旋转后的点。
   */
  private static rotatePointAroundAxis(
    point: THREE.Vector3,
    axisPoint: THREE.Vector3,
    axisDirection: THREE.Vector3,
    angleRad: number
  ): THREE.Vector3 {
    return axisPoint.clone().add(ReferencePlaneSolver.rotateAroundAxis(point.clone().sub(axisPoint), axisDirection, angleRad));
  }

  /**
   * 以垂直基准保持角度场景稳定。
   *
   * @param bundle 角度参考集合。
   * @param axisPoint 轴点。
   * @param angleDeg 目标角度。
   * @returns 可保持时返回结果，否则返回 `null`。
   */
  private static solveAngleByPerpendicularBaseline(
    bundle: AngleReferenceBundle,
    axisPoint: THREE.Vector3,
    angleDeg: number
  ): PlaneFeatureResult | null {
    const perpendicularBase = ReferencePlaneSolver.solveLinePerpendicularConstraint([bundle.lineEntity, bundle.planeEntity]);
    if (!perpendicularBase || perpendicularBase.solveStatus !== PlaneSolveStatus.FullyDefined || perpendicularBase.planes.length === 0) {
      return null;
    }
    const baseFrame = perpendicularBase.planes[0];
    const deltaAngleRad = ((Number.isFinite(angleDeg) ? angleDeg : 0) - 90) * Math.PI / 180;
    const rotatedOrigin = ReferencePlaneSolver.rotatePointAroundAxis(baseFrame.origin, axisPoint, bundle.axisDirection, deltaAngleRad);
    const rotatedNormal = ReferencePlaneSolver.rotateAroundAxis(baseFrame.normal, bundle.axisDirection, deltaAngleRad);
    return ReferencePlaneSolver.buildSuccessResult({
      frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(rotatedOrigin, rotatedNormal)],
      statusText: '完全定义',
      editableFields: [PlaneEditableField.Angle],
      availableConstraints: [PlaneConstraintType.Angle, PlaneConstraintType.Coincident],
    });
  }

  /**
   * 解析角度约束所需的线面参考组合。
   *
   * @param resolved 已解析实体。
   * @returns 角度参考集合；无法解析时返回 `null`。
   */
  private static resolveAngleReferenceBundle(resolved: PlaneResolvedEntity[]): AngleReferenceBundle | null {
    const first = resolved[0];
    const second = resolved[1];
    if (!first || !second) {
      return null;
    }
    const firstPlane = ReferencePlaneSolver.readPlane(first);
    const secondPlane = ReferencePlaneSolver.readPlane(second);
    const firstLineDirection = ReferencePlaneSolver.readDirection(first);
    const secondLineDirection = ReferencePlaneSolver.readDirection(second);
    if (firstPlane && secondLineDirection) {
      return { planeEntity: first, lineEntity: second, basePlane: firstPlane, axisDirection: secondLineDirection };
    }
    if (secondPlane && firstLineDirection) {
      return { planeEntity: second, lineEntity: first, basePlane: secondPlane, axisDirection: firstLineDirection };
    }
    return null;
  }

  /**
   * 求解距离偏移约束。
   *
   * @param resolved 已解析实体。
   * @param distanceValue 距离值。
   * @returns 求解结果。
   */
  private static solveDistanceOffset(resolved: PlaneResolvedEntity[], distanceValue: number): PlaneFeatureResult {
    const first = resolved[0];
    if (!first) {
      return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.UnderDefined, '距离建面至少需要一个平面参考');
    }
    const plane = ReferencePlaneSolver.readPlane(first);
    if (!plane) {
      return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.Inconsistent, '第一参考不是平面类实体');
    }
    const origin = ReferencePlaneSolver.resolveDistancePlaneOrigin(first, plane);
    const frame = ReferencePlaneSolver.buildPlaneFrame(origin, plane.normal);
    return ReferencePlaneSolver.buildSuccessResult({
      frameCandidates: [frame],
      statusText: '完全定义',
      editableFields: [PlaneEditableField.Distance, PlaneEditableField.PlaneCount],
      availableConstraints: [PlaneConstraintType.Distance, PlaneConstraintType.Coincident, PlaneConstraintType.Parallel],
      diagnostics: [{
        code: 'PLANE_DISTANCE_READY',
        message: Number.isFinite(distanceValue) ? '已使用距离参数求解' : '距离参数无效，按0处理',
        severity: ReferenceDiagnosticSeverity.Info,
      }],
    });
  }

  /**
   * 求解中间面约束。
   *
   * @param resolved 已解析实体。
   * @param angularEpsilon 角度容差。
   * @param reverseOffset 是否反向选择候选面。
   * @returns 求解结果。
   */
  private static solveMidPlane(
    resolved: PlaneResolvedEntity[],
    angularEpsilon = 1e-6,
    reverseOffset = false
  ): PlaneFeatureResult {
    const first = resolved[0];
    const second = resolved[1];
    if (!first || !second) {
      return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.UnderDefined, '中间基准面需要两个平面类参考');
    }

    const planeA = ReferencePlaneSolver.readPlane(first);
    const planeB = ReferencePlaneSolver.readPlane(second);
    if (!planeA || !planeB) {
      return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.Inconsistent, '所选参考不满足平面类约束');
    }

    const equationA = ReferencePlaneSolver.toPlaneEquation(planeA.normal, planeA.point);
    const equationB = ReferencePlaneSolver.toPlaneEquation(planeB.normal, planeB.point);
    const parallelEpsilon = Number.isFinite(angularEpsilon)
      ? Math.max(1e-9, Math.abs(angularEpsilon))
      : 1e-6;

    let frame: PlaneFrame | null = null;
    if (ReferencePlaneSolver.arePlaneNormalsParallel(equationA, equationB, parallelEpsilon)) {
      frame = ReferencePlaneSolver.buildParallelMidPlaneFrame(equationA, equationB);
    } else {
      const midpoint = equationA.point.clone().add(equationB.point).multiplyScalar(0.5);
      const candidates = ReferencePlaneSolver.buildIntersectingMidPlaneCandidates(equationA, equationB);
      frame = ReferencePlaneSolver.selectIntersectingMidPlaneCandidate(candidates, midpoint, reverseOffset);
    }

    if (!frame) {
      return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.NoSolution, '中间基准面求解失败：无法构造稳定角平分面');
    }

    return ReferencePlaneSolver.buildSuccessResult({
      frameCandidates: [frame],
      statusText: '完全定义',
      availableConstraints: [PlaneConstraintType.MidPlane, PlaneConstraintType.Distance, PlaneConstraintType.Coincident],
    });
  }

  /**
   * 判断两点是否近似重合。
   *
   * @param a 点 A。
   * @param b 点 B。
   * @param epsilon 容差。
   * @returns 是否近似重合。
   */
  private static almostEqualPoint(a: THREE.Vector3, b: THREE.Vector3, epsilon = 1e-9): boolean {
    return a.distanceToSquared(b) <= epsilon * epsilon;
  }

  /**
   * 归一化外环顶点集合。
   *
   * @param vertices 顶点集合。
   * @returns 归一化后的顶点集合。
   */
  private static normalizeLoopVertices(vertices: THREE.Vector3[]): THREE.Vector3[] {
    if (vertices.length === 0) {
      return [];
    }
    const normalized = vertices.map((item) => item.clone());
    if (normalized.length >= 2 && ReferencePlaneSolver.almostEqualPoint(normalized[0], normalized[normalized.length - 1])) {
      normalized.pop();
    }
    return normalized;
  }

  /**
   * 通过顶点集合估算平面中心。
   *
   * @param vertices 顶点集合。
   * @param planePoint 平面参考点。
   * @param planeNormal 平面法向。
   * @returns 中心点；无法估算时返回 `null`。
   */
  private static computePlaneMidpointFromVertices(
    vertices: THREE.Vector3[],
    planePoint: THREE.Vector3,
    planeNormal: THREE.Vector3
  ): THREE.Vector3 | null {
    const valid = ReferencePlaneSolver.normalizeLoopVertices(vertices);
    if (valid.length < 3) {
      return null;
    }

    const frame = ReferencePlaneSolver.buildPlaneFrame(planePoint, planeNormal);
    let minX = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    valid.forEach((point) => {
      const relative = point.clone().sub(frame.origin);
      const localX = relative.dot(frame.xDir);
      const localY = relative.dot(frame.yDir);
      minX = Math.min(minX, localX);
      maxX = Math.max(maxX, localX);
      minY = Math.min(minY, localY);
      maxY = Math.max(maxY, localY);
    });

    if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY) || !Number.isFinite(maxY)) {
      return null;
    }

    return frame.origin.clone()
      .addScaledVector(frame.xDir, (minX + maxX) * 0.5)
      .addScaledVector(frame.yDir, (minY + maxY) * 0.5);
  }

  /**
   * 解析距离建面的显示原点。
   *
   * @param first 第一参考实体。
   * @param fallbackPlane 兜底平面信息。
   * @returns 平面原点。
   */
  private static resolveDistancePlaneOrigin(
    first: PlaneResolvedEntity,
    fallbackPlane: PlanePointNormalGeometry
  ): THREE.Vector3 {
    const centerFromLoop = ReferencePlaneSolver.computePlaneMidpointFromVertices(
      ReferencePlaneSolver.readFaceOuterLoop(first),
      fallbackPlane.point,
      fallbackPlane.normal,
    );
    if (centerFromLoop) {
      return centerFromLoop;
    }
    return fallbackPlane.point.clone();
  }

  /**
   * 构造平面方程。
   *
   * @param normal 法向。
   * @param point 平面上一点。
   * @returns 平面方程。
   */
  private static toPlaneEquation(normal: THREE.Vector3, point: THREE.Vector3): PlaneEquation {
    const unitNormal = ReferencePlaneSolver.normalizeVec3(normal);
    return {
      normal: unitNormal,
      d: unitNormal.dot(point),
      point: point.clone(),
    };
  }

  /**
   * 对齐平面方程法向。
   *
   * @param equation 平面方程。
   * @param targetNormal 目标法向。
   * @returns 对齐后的平面方程。
   */
  private static alignPlaneEquationToNormal(equation: PlaneEquation, targetNormal: THREE.Vector3): PlaneEquation {
    if (equation.normal.dot(targetNormal) >= 0) {
      return equation;
    }
    return {
      normal: equation.normal.clone().multiplyScalar(-1),
      d: -equation.d,
      point: equation.point,
    };
  }

  /**
   * 判断两平面法向是否平行。
   *
   * @param first 平面方程 A。
   * @param second 平面方程 B。
   * @param epsilon 容差。
   * @returns 是否平行。
   */
  private static arePlaneNormalsParallel(first: PlaneEquation, second: PlaneEquation, epsilon: number): boolean {
    return first.normal.clone().cross(second.normal).length() <= epsilon;
  }

  /**
   * 构造平行平面的中分面。
   *
   * @param first 平面方程 A。
   * @param second 平面方程 B。
   * @returns 中分面坐标系。
   */
  private static buildParallelMidPlaneFrame(first: PlaneEquation, second: PlaneEquation): PlaneFrame {
    const alignedSecond = ReferencePlaneSolver.alignPlaneEquationToNormal(second, first.normal);
    const middleD = (first.d + alignedSecond.d) * 0.5;
    return ReferencePlaneSolver.buildPlaneFrame(first.point.clone().addScaledVector(first.normal, middleD - first.d), first.normal);
  }

  /**
   * 计算两平面交线上的一点。
   *
   * @param first 平面方程 A。
   * @param second 平面方程 B。
   * @returns 交线上一点；无法求解时返回 `null`。
   */
  private static computePlaneIntersectionPoint(first: PlaneEquation, second: PlaneEquation): THREE.Vector3 | null {
    const cross = first.normal.clone().cross(second.normal);
    const denominator = cross.dot(cross);
    if (denominator <= 1e-12) {
      return null;
    }
    const termA = second.normal.clone().cross(cross).multiplyScalar(first.d);
    const termB = cross.clone().cross(first.normal).multiplyScalar(second.d);
    return termA.add(termB).multiplyScalar(1 / denominator);
  }

  /**
   * 构造相交平面的角平分面候选。
   *
   * @param intersectionPoint 两平面交线上的点。
   * @param rawNormal 原始法向。
   * @param rawD 原始常数项。
   * @returns 候选平面；退化时返回 `null`。
   */
  private static buildIntersectingMidPlaneCandidate(
    intersectionPoint: THREE.Vector3,
    rawNormal: THREE.Vector3,
    rawD: number
  ): IntersectingMidPlaneCandidate | null {
    const rawLength = rawNormal.length();
    if (rawLength <= 1e-9) {
      return null;
    }
    const normal = ReferencePlaneSolver.normalizeVec3(rawNormal);
    const normalizedD = rawD / rawLength;
    const origin = intersectionPoint.clone().addScaledVector(normal, normalizedD - normal.dot(intersectionPoint));
    return {
      frame: ReferencePlaneSolver.buildPlaneFrame(origin, normal),
      normal,
      d: normalizedD,
    };
  }

  /**
   * 构造所有相交中分面候选。
   *
   * @param first 平面方程 A。
   * @param second 平面方程 B。
   * @returns 候选集合。
   */
  private static buildIntersectingMidPlaneCandidates(first: PlaneEquation, second: PlaneEquation): IntersectingMidPlaneCandidate[] {
    const intersectionPoint = ReferencePlaneSolver.computePlaneIntersectionPoint(first, second);
    if (!intersectionPoint) {
      return [];
    }
    const candidates: IntersectingMidPlaneCandidate[] = [];
    const plusCandidate = ReferencePlaneSolver.buildIntersectingMidPlaneCandidate(
      intersectionPoint,
      first.normal.clone().add(second.normal),
      first.d + second.d
    );
    if (plusCandidate) {
      candidates.push(plusCandidate);
    }
    const minusCandidate = ReferencePlaneSolver.buildIntersectingMidPlaneCandidate(
      intersectionPoint,
      first.normal.clone().sub(second.normal),
      first.d - second.d
    );
    if (minusCandidate) {
      candidates.push(minusCandidate);
    }
    return candidates;
  }

  /**
   * 计算点到平面的距离。
   *
   * @param point 点。
   * @param normal 平面法向。
   * @param d 平面方程常数项。
   * @returns 距离值。
   */
  private static distanceFromPointToPlane(point: THREE.Vector3, normal: THREE.Vector3, d: number): number {
    return Math.abs(normal.dot(point) - d);
  }

  /**
   * 从中分面候选中选择最终结果。
   *
   * @param candidates 候选集合。
   * @param midpoint 参考中点。
   * @param reverseOffset 是否反向选择。
   * @returns 选中的平面坐标系。
   */
  private static selectIntersectingMidPlaneCandidate(
    candidates: IntersectingMidPlaneCandidate[],
    midpoint: THREE.Vector3,
    reverseOffset: boolean
  ): PlaneFrame | null {
    if (candidates.length === 0) {
      return null;
    }
    if (candidates.length === 1) {
      return candidates[0].frame;
    }
    const firstDistance = ReferencePlaneSolver.distanceFromPointToPlane(midpoint, candidates[0].normal, candidates[0].d);
    const secondDistance = ReferencePlaneSolver.distanceFromPointToPlane(midpoint, candidates[1].normal, candidates[1].d);
    const defaultIndex = firstDistance <= secondDistance ? 0 : 1;
    const selectedIndex = reverseOffset ? (defaultIndex === 0 ? 1 : 0) : defaultIndex;
    return candidates[selectedIndex].frame;
  }

  /**
   * 判断是否为线面混合角度约束。
   *
   * @param definition 特征定义。
   * @param resolved 已解析实体。
   * @returns 是否为线面混合角度约束。
   */
  private static isLinePlaneMixedAngleConstraint(
    definition: PlaneFeatureDefinition,
    resolved: PlaneResolvedEntity[]
  ): boolean {
    const firstRoleRef = definition.refs.find((item) => item.role === PlaneReferenceRole.First);
    const secondRoleRef = definition.refs.find((item) => item.role === PlaneReferenceRole.Second);
    const isLinePlaneMixedAngle = Boolean(
      firstRoleRef
      && secondRoleRef
      && firstRoleRef.constraint === PlaneConstraintType.Coincident
      && secondRoleRef.constraint === PlaneConstraintType.Angle
      && ReferencePlaneSolver.readDirection(resolved[0]) !== null
      && ReferencePlaneSolver.readPlane(resolved[1]) !== null
    );
    const isPlaneLineMixedAngle = Boolean(
      firstRoleRef
      && secondRoleRef
      && firstRoleRef.constraint === PlaneConstraintType.Angle
      && secondRoleRef.constraint === PlaneConstraintType.Coincident
      && ReferencePlaneSolver.readPlane(resolved[0]) !== null
      && ReferencePlaneSolver.readDirection(resolved[1]) !== null
    );
    return isLinePlaneMixedAngle || isPlaneLineMixedAngle;
  }

  /**
   * 推断当前应执行的约束类型。
   *
   * @param definition 特征定义。
   * @param resolved 已解析实体。
   * @returns 约束类型。
   */
  private static inferConstraint(definition: PlaneFeatureDefinition, resolved: PlaneResolvedEntity[]): PlaneConstraintType {

      /**
     * 线+面混合约束识别：
     * 1. 第一参考写入重合（线保持重合语义）；
     * 2. 第二参考写入垂直（面进入垂直语义）；
     * 3. 求解分派仍需按“垂直”执行，不能被第一参考重合覆盖。
     */
    const firstRoleRef = definition.refs.find((item) => item.role === PlaneReferenceRole.First);
    const secondRoleRef = definition.refs.find((item) => item.role === PlaneReferenceRole.Second);
    const isLinePlaneMixedPerpendicular = Boolean(
      firstRoleRef
      && secondRoleRef
      && firstRoleRef.constraint === PlaneConstraintType.Coincident
      && secondRoleRef.constraint === PlaneConstraintType.Perpendicular
      && ReferencePlaneSolver.readDirection(resolved[0]) !== null
      && ReferencePlaneSolver.readPlane(resolved[1]) !== null
    );
    const isPlaneLineMixedPerpendicular = Boolean(
      firstRoleRef
      && secondRoleRef
      && firstRoleRef.constraint === PlaneConstraintType.Perpendicular
      && secondRoleRef.constraint === PlaneConstraintType.Coincident
      && ReferencePlaneSolver.readPlane(resolved[0]) !== null
      && ReferencePlaneSolver.readDirection(resolved[1]) !== null
    );
    if (isLinePlaneMixedPerpendicular || isPlaneLineMixedPerpendicular) {
      return PlaneConstraintType.Perpendicular;
    }

      if (ReferencePlaneSolver.isLinePlaneMixedAngleConstraint(definition, resolved)) {
      return PlaneConstraintType.Angle;
    }


      const firstRef = definition.refs.find((item) => item.role === PlaneReferenceRole.First);
    // 用户手动指定约束时优先使用，不再自动改写。
    if (firstRef && firstRef.constraint !== PlaneConstraintType.Auto) {
      return firstRef.constraint;
    }

    const first = resolved[0];
    const second = resolved[1];
    if (!first) {
      return PlaneConstraintType.Auto;
    }

    // 用类实例类型判断取代字符串 includes，保证类型安全。
    const isFirstPlanarPlane = ReferencePlaneSolver.isPlanarPlaneEntity(first);
    const isSecondPlanarPlane = second != null && ReferencePlaneSolver.isPlanarPlaneEntity(second);
    const isFirstSurface = ReferencePlaneSolver.isNonPlanarSurfaceEntity(first);
    const isSecondSurface = second != null && ReferencePlaneSolver.isNonPlanarSurfaceEntity(second);
    const isFirstPlaneForAngle = isFirstPlanarPlane || isFirstSurface;
    const isSecondPoint = second != null && ReferencePlaneSolver.isPointReferenceEntity(second);
    const isFirstPoint = ReferencePlaneSolver.isPointReferenceEntity(first);
    const isSecondLine = second != null && ReferencePlaneSolver.readDirection(second) !== null;
    const isSurfacePointPair = (isFirstSurface && isSecondPoint) || (isSecondSurface && isFirstPoint);

    // 以下是自动推断优先级。
    if (isFirstPlanarPlane && !second) {
      return PlaneConstraintType.Distance;
    }
    if (isFirstPlanarPlane && isSecondPlanarPlane) {
      return PlaneConstraintType.MidPlane;
    }
    if (isFirstPlaneForAngle && isSecondLine) {
      return PlaneConstraintType.Angle;
    }
    if (isFirstPlanarPlane && isSecondPoint) {
      return PlaneConstraintType.Parallel;
    }
    if (isSurfacePointPair) {
      return PlaneConstraintType.OnSurface;
    }
    return PlaneConstraintType.Coincident;
  }

  /**
   * 读取主标量值。
   *
   * @param definition 特征定义。
   * @returns 主标量值。
   */
  private static readPrimaryScalar(definition: PlaneFeatureDefinition): number {
    const firstRef = definition.refs.find((item) => item.role === PlaneReferenceRole.First);
    // 缺失标量时返回 0，保持求解稳定。
    if (!firstRef || !Number.isFinite(firstRef.scalar)) {
      return 0;
    }
    return Number(firstRef.scalar);
  }

  /**
   * 读取角度标量值。
   *
   * @param definition 特征定义。
   * @returns 角度值。
   */
  private static readAngleScalar(definition: PlaneFeatureDefinition): number {
    const roleRank: Record<PlaneReferenceRole, number> = {
      [PlaneReferenceRole.First]: 0,
      [PlaneReferenceRole.Second]: 1,
      [PlaneReferenceRole.Third]: 2,
    };
    const angleRef = [...definition.refs]
      .sort((a, b) => (roleRank[a.role] ?? 99) - (roleRank[b.role] ?? 99))
      .find((item) => (
        item.constraint === PlaneConstraintType.Angle
        && Number.isFinite(item.scalar)
      ));
    if (angleRef && Number.isFinite(angleRef.scalar)) {
      return Number(angleRef.scalar);
    }

    const legacyAngleValue = (definition as { angleValue?: unknown }).angleValue;
    if (Number.isFinite(legacyAngleValue)) {
      return Number(legacyAngleValue);
    }
    return 0;
  }

  /**
   * 解析角度旋转轴上的参考点。
   *
   * @param lineEntity 线性实体。
   * @param fallbackPoint 兜底点。
   * @returns 轴点。
   */
  private static resolveAngleRotationAxisPoint(
    lineEntity: PlaneResolvedEntity,
    fallbackPoint: THREE.Vector3
  ): THREE.Vector3 {
    // 优先读取两端点并计算中点。
    const endpoints = ReferencePlaneSolver.readLineEndpoints(lineEntity);
    if (endpoints) {
      return endpoints.start.clone().add(endpoints.end).multiplyScalar(0.5);
    }
    const point = ReferencePlaneSolver.readPoint(lineEntity);
    if (point) {
      return point;
    }
    return fallbackPoint.clone();
  }

  /**
   * 解析角度阵列使用的旋转轴。
   *
   * @param entities 已解析实体。
   * @param fallbackPoint 兜底点。
   * @returns 旋转轴信息；无法解析时返回 `null`。
   */
  private static resolveAngleRotationAxis(
    entities: PlaneResolvedEntity[],
    fallbackPoint: THREE.Vector3
  ): AngleRotationAxis | null {
    const lineEntity = entities.find((item) => ReferencePlaneSolver.readDirection(item) !== null);
    if (!lineEntity) {
      return null;
    }
    const direction = ReferencePlaneSolver.readDirection(lineEntity);
    if (!direction) {
      return null;
    }
    return {
      point: ReferencePlaneSolver.resolveAngleRotationAxisPoint(lineEntity, fallbackPoint),
      direction,
    };
  }

  /**
   * 按距离或角度扩展多平面结果。
   *
   * @param result 当前结果。
   * @param definition 特征定义。
   * @param constraint 当前约束。
   * @param resolved 已解析实体。
   * @param scalar 标量值。
   * @returns 扩展后的结果。
   */
  private static expandOffsetPlanes(
    result: PlaneFeatureResult,
    definition: PlaneFeatureDefinition,
    constraint: PlaneConstraintType,
    resolved: PlaneResolvedEntity[],
    scalar: number
  ): PlaneFeatureResult {
    const count = Math.max(1, Math.floor(Number.isFinite(definition.planeCount) ? definition.planeCount : 1));
    if (constraint !== PlaneConstraintType.Distance && constraint !== PlaneConstraintType.Angle) {
      return result;
    }
    if (result.planes.length !== 1) {
      return result;
    }

    if (constraint === PlaneConstraintType.Angle) {
      if (count <= 1) {
        return {
          ...result,
          editableFields: Array.from(new Set([...result.editableFields, PlaneEditableField.PlaneCount])),
        };
      }
      const axis = ReferencePlaneSolver.resolveAngleRotationAxis(resolved, result.planes[0].origin);
      if (!axis) {
        return {
          ...result,
          editableFields: Array.from(new Set([...result.editableFields, PlaneEditableField.PlaneCount])),
        };
      }
      const stepRad = (Number.isFinite(scalar) ? scalar : 0) * Math.PI / 180;
      const base = result.planes[0];
      const planes = Array.from({ length: count }).map((_, index) => {
        if (index === 0) {
          return base;
        }
        const angleRad = stepRad * index;
        const rotatedOrigin = ReferencePlaneSolver.rotatePointAroundAxis(base.origin, axis.point, axis.direction, angleRad);
        const rotatedNormal = ReferencePlaneSolver.rotateAroundAxis(base.normal, axis.direction, angleRad);
        return ReferencePlaneSolver.buildPlaneFrame(rotatedOrigin, rotatedNormal);
      });
      return {
        ...result,
        planes,
        editableFields: Array.from(new Set([...result.editableFields, PlaneEditableField.PlaneCount])),
      };
    }

    const distance = Number.isFinite(scalar) ? scalar : 0;
    const base = result.planes[0];
    // 以主平面为基准沿法向扩展阵列平面。
    // 关键规则：
    // 1. 即使 count=1，也必须生成“偏移后的第一张平面”；
    // 2. 不允许把原参考面当作第一个输出平面（对齐 SolidWorks 偏移语义）。
    const planes = Array.from({ length: count }).map((_, index) => {

        /**
       * 偏移距离语义：
       * 1. 正值沿参考面法线正方向偏移；
       * 2. 负值沿参考面法线反方向偏移；
       * 3. Distance 模式不再额外叠加 reverseOffset 复选语义，避免与数值符号冲突。
       */
      const offset = (index + 1) * distance;

        return ReferencePlaneSolver.buildPlaneFrame(base.origin.clone().addScaledVector(base.normal, offset), base.normal);
    });

    return {
      ...result,
      planes,
      editableFields: Array.from(new Set([...result.editableFields, PlaneEditableField.PlaneCount])),
    };
  }

  /**
   * 应用法向翻转。
   *
   * @param result 当前结果。
   * @param definition 特征定义。
   * @returns 处理后的结果。
   */
  private static applyNormalReverse(result: PlaneFeatureResult, definition: PlaneFeatureDefinition): PlaneFeatureResult {
    const firstRef = definition.refs.find((item) => item.role === PlaneReferenceRole.First);
    if (!firstRef?.flags?.reverseNormal) {
      return result;
    }
    // 对每个候选平面执行法向翻转。
    const reversed = result.planes.map((plane) => ReferencePlaneSolver.buildPlaneFrame(plane.origin, plane.normal.clone().multiplyScalar(-1)));
    return {
      ...result,
      planes: reversed,
    };
  }

  /**
   * 收集实体显示尺寸参考点。
   *
   * @param entity 已解析实体。
   * @returns 参考点集合。
   */
  private static collectDisplayHintPoints(entity: PlaneResolvedEntity | undefined): THREE.Vector3[] {
    if (!entity) {
      return [];
    }


      // 正式基准面需要从自身面片读取尺寸参考点，避免新建基准面回退到默认 0.2 场景单位 (即 200 mm)。
    if (ReferencePlaneSolver.isDatumPlaneEntity(entity)) {
      return ReferencePlaneSolver.readDatumPlaneWorldVertices(entity);
    }


      const points: THREE.Vector3[] = [];
    const dedupeKeys = new Set<string>();

    /**
     * 追加基准面显示尺寸参考点。
     *
     * @param point 候选世界坐标点。
     */
    const appendPoint = (point: THREE.Vector3): void => {
      const key = `${point.x.toFixed(9)}|${point.y.toFixed(9)}|${point.z.toFixed(9)}`;
      if (dedupeKeys.has(key)) {
        return;
      }
      dedupeKeys.add(key);
      points.push(point.clone());
    };

    // 首先读取面边界顶点集，保留外轮廓对显示尺寸的贡献。
    const outer = ReferencePlaneSolver.readFaceOuterLoop(entity);
    outer.forEach((item) => {
      appendPoint(item);
    });

    // 再读取基准面专用面片点，避免环形端面或不规则平面只用稀疏边界点导致显示面变形。
    ReferencePlaneSolver.readReferencePlaneFeatureFaceSurfaceVertices(entity).forEach((item) => {
      appendPoint(item);
    });

    if (points.length >= 3) {
      return points;
    }

    // 非 FeatureFace 或专用读取失败时保留原有兜底路径，不改变其它曲面求解逻辑。
    return ReferencePlaneSolver.readSurfaceWorldVertices(entity);
  }

  /**
   * 收集线性实体显示尺寸参考点。
   *
   * @param entity 已解析实体。
   * @returns 参考点集合。
   */
  private static collectLinearDisplayHintPoints(entity: PlaneResolvedEntity | undefined): THREE.Vector3[] {
    if (!entity) {
      return [];
    }

    const direction = ReferencePlaneSolver.readDirection(entity);
    if (!direction) {
      return [];
    }

    // 优先读取端点对。
    const endpoints = ReferencePlaneSolver.readLineEndpoints(entity);
    if (endpoints) {
      return [endpoints.start.clone(), endpoints.end.clone()];
    }

    const origin = ReferencePlaneSolver.readPoint(entity);
    if (!origin) {
      return [];
    }

    // 端点缺失时用“原点 + 单位方向”构造最小线段，保证尺寸可估算。
    return [
      origin.clone(),
      origin.clone().add(direction),
    ];
  }

  /**
   * 收集点性实体显示参考点。
   *
   * @param resolved 已解析实体集合。
   * @returns 点集合。
   */
  private static collectPointDisplayHintPoints(resolved: PlaneResolvedEntity[]): THREE.Vector3[] {
    const points: THREE.Vector3[] = [];
    resolved.forEach((entity) => {
      const point = ReferencePlaneSolver.readPoint(entity);
      if (!point) {
        return;
      }
      points.push(point);
    });
    return points;
  }

  /**
   * 判断是否为三点重合显示场景。
   *
   * @param resolved 已解析实体。
   * @param constraint 当前约束。
   * @returns 是否命中。
   */
  private static isThreePointCoincidentScenario(
    resolved: PlaneResolvedEntity[],
    constraint: PlaneConstraintType
  ): boolean {
    if (constraint !== PlaneConstraintType.Coincident) {
      return false;
    }
    const points = ReferencePlaneSolver.collectPointDisplayHintPoints(resolved);
    return points.length >= 3;
  }

  /**
   * 判断是否为双线重合显示场景。
   *
   * @param resolved 已解析实体。
   * @param constraint 当前约束。
   * @returns 是否命中。
   */
  private static isTwoLinearCoincidentScenario(
    resolved: PlaneResolvedEntity[],
    constraint: PlaneConstraintType
  ): boolean {
    if (constraint !== PlaneConstraintType.Coincident) {
      return false;
    }
    return Boolean(ReferencePlaneSolver.readDirection(resolved[0]) && ReferencePlaneSolver.readDirection(resolved[1]));
  }

  /**
   * 判断是否为线面垂直显示场景。
   *
   * @param resolved 已解析实体。
   * @param constraint 当前约束。
   * @returns 是否命中。
   */
  private static isLinePlanePerpendicularScenario(
    resolved: PlaneResolvedEntity[],
    constraint: PlaneConstraintType
  ): boolean {
    if (constraint !== PlaneConstraintType.Perpendicular) {
      return false;
    }
    return Boolean(ReferencePlaneSolver.resolveLinePlanePerpendicularReferences(resolved));
  }

  /**
   * 解析线面垂直显示所需引用。
   *
   * @param resolved 已解析实体。
   * @returns 线面引用；无法解析时返回 `null`。
   */
  private static resolveLinePlanePerpendicularReferences(
    resolved: PlaneResolvedEntity[]
  ): LinePlanePerpendicularReferences | null {
    const first = resolved[0];
    const second = resolved[1];
    if (!first || !second) {
      return null;
    }

    if (ReferencePlaneSolver.readDirection(first) && ReferencePlaneSolver.readPlane(second)) {
      return { line: first, plane: second };
    }
    if (ReferencePlaneSolver.readPlane(first) && ReferencePlaneSolver.readDirection(second)) {
      return { line: second, plane: first };
    }
    return null;
  }

  /**
   * 解析线段几何。
   *
   * @param entity 已解析实体。
   * @returns 线段几何；无法解析时返回 `null`。
   */
  private static resolveLineSegmentGeometry(entity: PlaneResolvedEntity | undefined): LineSegmentGeometry | null {
    if (!entity) {
      return null;
    }

    // 优先使用类提供的两端点。
    const endpoints = ReferencePlaneSolver.readLineEndpoints(entity);
    if (endpoints) {
      const segment = endpoints.end.clone().sub(endpoints.start);
      const length = segment.length();
      if (length <= 1e-9) {
        return null;
      }
      return {
        start: endpoints.start,
        end: endpoints.end,
        midpoint: endpoints.start.clone().add(endpoints.end).multiplyScalar(0.5),
        length,
      };
    }

    const origin = ReferencePlaneSolver.readPoint(entity);
    const direction = ReferencePlaneSolver.readDirection(entity);
    if (!origin || !direction) {
      return null;
    }
    const endPoint = origin.clone().add(direction);
    return {
      start: origin,
      end: endPoint,
      midpoint: origin.clone().add(endPoint).multiplyScalar(0.5),
      length: endPoint.clone().sub(origin).length(),
    };
  }

  /**
   * 调整线面垂直场景的显示布局。
   *
   * @param planes 平面集合。
   * @param resolved 已解析实体。
   * @param options 布局选项。
   * @returns 调整后的平面集合。
   */
  private static applyLinePlanePerpendicularDisplayLayout(
    planes: PlaneFeatureResult['planes'],
    resolved: PlaneResolvedEntity[],
    options: ApplyLinePlanePerpendicularDisplayLayoutOptions = {}
  ): PlaneFeatureResult['planes'] {
    const references = ReferencePlaneSolver.resolveLinePlanePerpendicularReferences(resolved);
    if (!references) {
      return planes;
    }

    const lineSegment = ReferencePlaneSolver.resolveLineSegmentGeometry(references.line);
    const secondPlane = ReferencePlaneSolver.readPlane(references.plane);
    if (!lineSegment) {
      return planes;
    }

    const preserveAngularSequence = Boolean(options.preserveAngularSequence);
    let previousYDir: THREE.Vector3 | null = null;
    return planes.map((plane) => {
      const lineDirection = ReferencePlaneSolver.normalizeVec3(lineSegment.end.clone().sub(lineSegment.start));
      let yDir = ReferencePlaneSolver.normalizeVec3(plane.normal.clone().cross(lineDirection));
      if (yDir.length() <= 1e-9) {
        yDir = ReferencePlaneSolver.normalizeVec3(plane.yDir);
      }

      if (preserveAngularSequence) {
        if (previousYDir && yDir.dot(previousYDir) < 0) {
          yDir = yDir.clone().multiplyScalar(-1);
        }
      } else if (secondPlane && yDir.dot(secondPlane.normal) < 0) {
        yDir = yDir.clone().multiplyScalar(-1);
      }

      const displayWidth = applyReferencePlaneAutosizeMargin(lineSegment.length);
      // 线面组合只有所选线提供稳定局部跨度，两轴统一使用线长，禁止继承整模兜底高度。
      const displayHeight = displayWidth;
      const origin = lineSegment.midpoint.clone().addScaledVector(yDir, displayHeight * 0.5);
      const normal = ReferencePlaneSolver.normalizeVec3(lineDirection.clone().cross(yDir));
      previousYDir = yDir;

      return {
        ...plane,
        origin,
        normal,
        xDir: lineDirection,
        yDir,
        displayWidth,
        displayHeight,
      };
    });
  }

  /**
   * 按约束收集显示参考点。
   *
   * @param resolved 已解析实体。
   * @param constraint 当前约束。
   * @returns 参考点集合。
   */
  private static collectDisplayHintPointsByConstraint(
    resolved: PlaneResolvedEntity[],
    constraint: PlaneConstraintType
  ): THREE.Vector3[] {
    if (constraint === PlaneConstraintType.Parallel) {
      const planePoints = resolved
        .filter((entity) => ReferencePlaneSolver.readPlane(entity) !== null)
        .flatMap((entity) => ReferencePlaneSolver.collectDisplayHintPoints(entity));
      if (planePoints.length > 0) {
        return planePoints;
      }
    }

    if (ReferencePlaneSolver.isTwoLinearCoincidentScenario(resolved, constraint)) {
      const firstLinePoints = ReferencePlaneSolver.collectLinearDisplayHintPoints(resolved[0]);
      const secondLinePoints = ReferencePlaneSolver.collectLinearDisplayHintPoints(resolved[1]);
      return [...firstLinePoints, ...secondLinePoints];
    }

    // 三点重合场景应使用全部点参考来约束显示尺寸。
    if (ReferencePlaneSolver.isThreePointCoincidentScenario(resolved, constraint)) {
      return ReferencePlaneSolver.collectPointDisplayHintPoints(resolved);
    }

    const firstPoints = ReferencePlaneSolver.collectDisplayHintPoints(resolved[0]);
    if (constraint !== PlaneConstraintType.MidPlane) {
      return firstPoints;
    }

    // 两侧对称显示尺寸应同时覆盖第一与第二参考。
    const secondPoints = ReferencePlaneSolver.collectDisplayHintPoints(resolved[1]);
    return [...firstPoints, ...secondPoints];
  }

  /**
   * 计算参考点在局部平面坐标系中的范围。
   *
   * @param points 参考点集合。
   * @param plane 平面局部坐标系。
   * @returns 局部范围；无法计算时返回 `null`。
   */
  private static computeLocalExtent(
    points: THREE.Vector3[],
    plane: { origin: THREE.Vector3; xDir: THREE.Vector3; yDir: THREE.Vector3 }
  ): LocalPlaneExtent | null {
    if (points.length === 0) {
      return null;
    }
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    points.forEach((point) => {
      const relative = point.clone().sub(plane.origin);
      const localX = relative.dot(plane.xDir);
      const localY = relative.dot(plane.yDir);
      minX = Math.min(minX, localX);
      maxX = Math.max(maxX, localX);
      minY = Math.min(minY, localY);
      maxY = Math.max(maxY, localY);
    });
    const width = maxX - minX;
    const height = maxY - minY;
    if (!Number.isFinite(width) || !Number.isFinite(height)) {
      return null;
    }
    if (width <= 1e-9 || height <= 1e-9) {
      return null;
    }
    const centerX = (minX + maxX) * 0.5;
    const centerY = (minY + maxY) * 0.5;
    return {
      minX,
      maxX,
      minY,
      maxY,
      width,
      height,
      centerX,
      centerY,
    };
  }

  /**
   * 从模型包围盒构建八个角点。
   *
   * @param bounds 模型包围盒快照。
   * @returns 包围盒角点集合。
   */
  private static buildModelBoundingBoxCornerPoints(bounds: ReferencePlaneModelBoundingBox): THREE.Vector3[] {
    const { min, max } = bounds;
    return [
      new THREE.Vector3(min.x, min.y, min.z),
      new THREE.Vector3(max.x, min.y, min.z),
      new THREE.Vector3(min.x, max.y, min.z),
      new THREE.Vector3(max.x, max.y, min.z),
      new THREE.Vector3(min.x, min.y, max.z),
      new THREE.Vector3(max.x, min.y, max.z),
      new THREE.Vector3(min.x, max.y, max.z),
      new THREE.Vector3(max.x, max.y, max.z),
    ];
  }

  /**
   * 计算模型包围盒在目标平面局部坐标中的显示范围。
   *
   * @param bounds 模型包围盒快照。
   * @param plane 目标平面坐标系。
   * @returns 局部显示范围；包围盒无效时返回 null。
   */
  private static computeModelBoundingBoxLocalExtent(
    bounds: ReferencePlaneModelBoundingBox | null,
    plane: PlaneFrame,
  ): LocalPlaneExtent | null {
    if (!bounds) {
      return null;
    }
    const cornerPoints = ReferencePlaneSolver.buildModelBoundingBoxCornerPoints(bounds);
    return ReferencePlaneSolver.computeLocalExtent(cornerPoints, plane);
  }

  /**
   * 计算“曲面相切 + 平面垂直”场景的专用显示布局。
   *
   * 设计说明：
   * 1. 宽度沿曲面在切平面上的展开方向计算；
   * 2. 高度沿参考平面法向映射到切平面后的直径方向计算；
   * 3. 显示中心对齐到曲面宽度的中点，避免生成结果贴在曲面端点。
   *
   * @param plane 当前候选平面。
   * @param surfaceEntity 曲面实体。
   * @param planeNormal 参考平面法向。
   * @returns 专用显示布局；无法计算时返回 `null`。
   */
  private static resolveSurfacePlanePerpendicularDisplayLayout(
    plane: PlaneFrame,
    surfaceEntity: PlaneResolvedEntity,
    planeNormal: THREE.Vector3,
  ): SurfacePlanePerpendicularDisplayLayout | null {
    const surfacePoints = ReferencePlaneSolver.collectDisplayHintPoints(surfaceEntity);
    if (surfacePoints.length === 0) {
      return null;
    }

    const widthDirection = plane.normal.clone().cross(planeNormal).normalize();
    if (widthDirection.lengthSq() <= 1e-12) {
      return null;
    }

    const heightDirection = plane.normal.clone().cross(widthDirection).normalize();
    if (heightDirection.lengthSq() <= 1e-12) {
      return null;
    }

    const extent = ReferencePlaneSolver.computeLocalExtent(surfacePoints, {
      origin: plane.origin,
      xDir: widthDirection,
      yDir: heightDirection,
    });
    if (!extent) {
      ReferencePlaneSolver.logDisplaySizeDebug('曲面垂直布局范围无效', {
        hintPointCount: surfacePoints.length,
        sceneUnitScale: REFERENCE_PLANE_MM_TO_SCENE_UNIT_SCALE,
        planeOrigin: ReferencePlaneSolver.toDebugVec3(plane.origin),
        widthDirection: ReferencePlaneSolver.toDebugVec3(widthDirection),
        heightDirection: ReferencePlaneSolver.toDebugVec3(heightDirection),
      });
      return null;
    }

    ReferencePlaneSolver.logDisplaySizeDebug('曲面垂直布局范围与缩放', {
      hintPointCount: surfacePoints.length,
      extent,
      sceneUnitScale: REFERENCE_PLANE_MM_TO_SCENE_UNIT_SCALE,
      marginScale: REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE,
      displayWidth: applyReferencePlaneAutosizeMargin(extent.width),
      displayHeight: applyReferencePlaneAutosizeMargin(extent.height),
      planeOrigin: ReferencePlaneSolver.toDebugVec3(plane.origin),
      widthDirection: ReferencePlaneSolver.toDebugVec3(widthDirection),
      heightDirection: ReferencePlaneSolver.toDebugVec3(heightDirection),
    });

    return {
      width: applyReferencePlaneAutosizeMargin(extent.width),
      height: applyReferencePlaneAutosizeMargin(extent.height),
      origin: plane.origin.clone()
        .addScaledVector(widthDirection, extent.centerX)
        .addScaledVector(heightDirection, extent.centerY),
      widthDirection,
      heightDirection,
    };
  }

  /**
   * 解析“曲面 + 点相切”场景的专用显示布局。
   *
   * @param plane 当前候选平面。
   * @param surfaceEntity 曲面实体。
   * @param targetPoint 结果面必须经过的点。
   * @returns 专用显示布局；无法计算时返回 `null`。
   */
  private static resolveSurfacePointTangentDisplayLayout(
    plane: PlaneFrame,
    surfaceEntity: PlaneResolvedEntity,
    targetPoint: THREE.Vector3,
  ): PlaneFrame | null {
    const surfacePoints = ReferencePlaneSolver.collectDisplayHintPoints(surfaceEntity);
    if (surfacePoints.length === 0) {
      return null;
    }

    // 这里固定沿求解平面自身基向量显示，避免随视图旋转导致“斜着”的视觉跳变。
    const xDir = plane.xDir.clone().normalize();
    const yDir = plane.yDir.clone().normalize();
    if (xDir.lengthSq() <= 1e-12 || yDir.lengthSq() <= 1e-12) {
      return null;
    }

    const extent = ReferencePlaneSolver.computeLocalExtent([...surfacePoints, targetPoint.clone()], {
      origin: plane.origin,
      xDir,
      yDir,
    });
    if (!extent) {
      ReferencePlaneSolver.logDisplaySizeDebug('曲面相切布局范围无效', {
        hintPointCount: surfacePoints.length + 1,
        sceneUnitScale: REFERENCE_PLANE_MM_TO_SCENE_UNIT_SCALE,
        planeOrigin: ReferencePlaneSolver.toDebugVec3(plane.origin),
        xDir: ReferencePlaneSolver.toDebugVec3(xDir),
        yDir: ReferencePlaneSolver.toDebugVec3(yDir),
      });
      return null;
    }


      // SolidWorks 基准面按参考几何的宽高分别自动定尺寸，不能用最大边强制生成正方形。
    const displayWidth = applyReferencePlaneAutosizeMargin(extent.width);
    const displayHeight = applyReferencePlaneAutosizeMargin(extent.height);


      ReferencePlaneSolver.logDisplaySizeDebug('曲面相切布局范围与缩放', {
      hintPointCount: surfacePoints.length + 1,
      extent,
      sceneUnitScale: REFERENCE_PLANE_MM_TO_SCENE_UNIT_SCALE,
      marginScale: REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE,
      displayWidth,
      displayHeight,
      planeOrigin: ReferencePlaneSolver.toDebugVec3(plane.origin),
      xDir: ReferencePlaneSolver.toDebugVec3(xDir),
      yDir: ReferencePlaneSolver.toDebugVec3(yDir),
    });

    return {
      ...plane,
      origin: plane.origin.clone()
        .addScaledVector(xDir, extent.centerX)
        .addScaledVector(yDir, extent.centerY),
      xDir,
      yDir,
      displayWidth,
      displayHeight,
    };
  }

  /**
   * 根据约束类型与引用组合解析显示尺寸策略。
   *
   * 设计说明：
   * 1. 基准面命令已经以 `PlaneConstraintType` 作为唯一模式状态；
   * 2. 三点、两线等显示差异属于同一约束下的引用组合差异，不应额外引入第二套推断枚举；
   * 3. 这里只从已解析参考实体收集几何点，不允许通过模型整体范围生成兜底尺寸。
   *
   * @param constraint 当前实际求解约束。
   * @param resolved 已解析引用实体。
   * @param preserveLinePlanePerpendicularDisplay 是否沿用线面垂直显示。
   * @returns 显示尺寸策略。
   */
  private static resolveDisplaySizePolicy(
    constraint: PlaneConstraintType,
    resolved: PlaneResolvedEntity[],
    preserveLinePlanePerpendicularDisplay: boolean
  ): PlaneDisplaySizePolicy {

      // 正式基准面作为尺寸参考时必须保持同尺寸，不能套用普通面的显示外扩系数。
    const datumPlaneMarginScale = resolved.some((entity) => ReferencePlaneSolver.isDatumPlaneEntity(entity))
      ? 1
      : null;


      switch (constraint) {
      case PlaneConstraintType.MidPlane:
        return {
          effectiveConstraint: PlaneConstraintType.MidPlane,
          marginScale: datumPlaneMarginScale ?? REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE,
          recenterOrigin: true,
        };
      case PlaneConstraintType.Coincident:
        if (ReferencePlaneSolver.isThreePointCoincidentScenario(resolved, constraint)) {
          return {
            effectiveConstraint: PlaneConstraintType.Coincident,
            marginScale: REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE,
            recenterOrigin: true,
          };
        }
        if (ReferencePlaneSolver.isTwoLinearCoincidentScenario(resolved, constraint)) {
          return {
            effectiveConstraint: PlaneConstraintType.Coincident,
            marginScale: REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE,
            recenterOrigin: true,
          };
        }
        return {
          effectiveConstraint: PlaneConstraintType.Coincident,
          marginScale: datumPlaneMarginScale ?? REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE,
          recenterOrigin: false,
        };
      case PlaneConstraintType.Angle:
        return {
          effectiveConstraint: preserveLinePlanePerpendicularDisplay
            ? PlaneConstraintType.Perpendicular
            : PlaneConstraintType.Angle,
          marginScale: datumPlaneMarginScale ?? REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE,
          recenterOrigin: false,
        };
      case PlaneConstraintType.Project:
        return {
          effectiveConstraint: PlaneConstraintType.Project,
          marginScale: datumPlaneMarginScale ?? REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE,
          recenterOrigin: false,
        };
      case PlaneConstraintType.OnSurface:
        return {
          effectiveConstraint: PlaneConstraintType.OnSurface,
          marginScale: datumPlaneMarginScale ?? REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE,
          recenterOrigin: false,
        };
      case PlaneConstraintType.Auto:
      case PlaneConstraintType.Distance:
      case PlaneConstraintType.Parallel:
      case PlaneConstraintType.Perpendicular:
      case PlaneConstraintType.Normal:
      case PlaneConstraintType.Tangent:
        return {
          effectiveConstraint: preserveLinePlanePerpendicularDisplay
            ? PlaneConstraintType.Perpendicular
            : constraint,
          marginScale: datumPlaneMarginScale ?? REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE,
          recenterOrigin: false,
        };
    }
  }

  /**
   * 应用显示尺寸提示。
   *
   * @param result 当前结果。
   * @param resolved 已解析实体。
   * @param constraint 当前约束。
   * @param preserveLinePlanePerpendicularDisplay 是否沿用线面垂直显示。
   * @param modelBoundingBox 当前模型几何包围盒，用于缺少局部参考轮廓时兜底。
   * @returns 调整后的结果。
   */
  private static applyDisplaySizeHint(
    result: PlaneFeatureResult,
    resolved: PlaneResolvedEntity[],
    constraint: PlaneConstraintType,
    preserveLinePlanePerpendicularDisplay = false,
    modelBoundingBox: ReferencePlaneModelBoundingBox | null = null,
  ): PlaneFeatureResult {
    if (result.planes.length === 0) {
      return result;
    }

    const displayPolicy = ReferencePlaneSolver.resolveDisplaySizePolicy(
      constraint,
      resolved,
      preserveLinePlanePerpendicularDisplay,
    );

    const surfacePlaneReferenceBundle = displayPolicy.effectiveConstraint === PlaneConstraintType.Perpendicular
      ? ReferencePlaneSolver.resolveSurfacePlaneReferenceBundle(resolved)
      : null;
    if (surfacePlaneReferenceBundle) {
      const adjustedPlanes = result.planes.map((plane) => {
        const layout = ReferencePlaneSolver.resolveSurfacePlanePerpendicularDisplayLayout(
          plane,
          surfacePlaneReferenceBundle.surfaceEntity,
          surfacePlaneReferenceBundle.plane.normal,
        );
        if (!layout) {
          return plane;
        }

        return {
          ...plane,
          origin: layout.origin,
          xDir: layout.widthDirection,
          yDir: layout.heightDirection,
          displayWidth: layout.width,
          displayHeight: layout.height,
        };
      });

      return {
        ...result,
        planes: adjustedPlanes,
      };
    }

    const surfacePointReferenceBundle = displayPolicy.effectiveConstraint === PlaneConstraintType.Tangent
      ? ReferencePlaneSolver.resolveSurfacePointReferenceBundle(resolved)
      : null;
    if (surfacePointReferenceBundle) {
      const adjustedPlanes = result.planes.map((plane) => (
        ReferencePlaneSolver.resolveSurfacePointTangentDisplayLayout(
          plane,
          surfacePointReferenceBundle.surfaceEntity,
          surfacePointReferenceBundle.point,
        ) ?? plane
      ));

      return {
        ...result,
        planes: adjustedPlanes,
      };
    }


      /**
     * 线面垂直及其角度延续场景必须先执行局部专用布局：
     * 1. 线的两个端点在目标平面内只能形成一维范围，通用范围计算必然得到零高度；
     * 2. 若先进入通用退化兜底，整个模型包围盒高度会污染局部基准面；
     * 3. 专用布局已经以所选线长同时确定宽高，因此无需再经过通用尺寸估算。
     */
    const shouldApplyLinePlanePerpendicularLayout = (
      ReferencePlaneSolver.isLinePlanePerpendicularScenario(resolved, displayPolicy.effectiveConstraint)
      || preserveLinePlanePerpendicularDisplay
    );
    if (shouldApplyLinePlanePerpendicularLayout) {
      const preserveAngularSequence = (
        preserveLinePlanePerpendicularDisplay
        && constraint === PlaneConstraintType.Angle
        && result.planes.length > 1
      );
      return {
        ...result,
        planes: ReferencePlaneSolver.applyLinePlanePerpendicularDisplayLayout(result.planes, resolved, {
          preserveAngularSequence,
        }),
      };
    }


      const hintPoints = ReferencePlaneSolver.collectDisplayHintPointsByConstraint(resolved, displayPolicy.effectiveConstraint);
    const hasImportedTopologyReference = ReferencePlaneSolver.hasImportedTopologyReference(resolved);

    const planes = result.planes.map((plane, planeIndex) => {
      const extent = ReferencePlaneSolver.computeLocalExtent(hintPoints, plane);
      if (!extent) {
        const modelExtent = ReferencePlaneSolver.computeModelBoundingBoxLocalExtent(modelBoundingBox, plane);
        if (modelExtent) {
          const adjustedOrigin = displayPolicy.recenterOrigin
            ? plane.origin.clone().addScaledVector(plane.xDir, modelExtent.centerX).addScaledVector(plane.yDir, modelExtent.centerY)
            : plane.origin.clone();
          const displayWidth = modelExtent.width * REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE;
          const displayHeight = modelExtent.height * REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE;

          ReferencePlaneSolver.logDisplaySizeDebug('普通布局无参考轮廓，按模型包围盒兜底', {
            planeIndex,
            constraint,
            effectiveConstraint: displayPolicy.effectiveConstraint,
            hintPointCount: hintPoints.length,
            hasImportedTopologyReference,
            sceneUnitScale: REFERENCE_PLANE_MM_TO_SCENE_UNIT_SCALE,
            marginScale: REFERENCE_PLANE_AUTOSIZE_MARGIN_SCALE,
            extent: modelExtent,
            displayWidth,
            displayHeight,
            originalPlaneOrigin: ReferencePlaneSolver.toDebugVec3(plane.origin),
            adjustedOrigin: ReferencePlaneSolver.toDebugVec3(adjustedOrigin),
            xDir: ReferencePlaneSolver.toDebugVec3(plane.xDir),
            yDir: ReferencePlaneSolver.toDebugVec3(plane.yDir),
          });

          return {
            ...plane,
            origin: adjustedOrigin,
            displayWidth,
            displayHeight,
          };
        }

        ReferencePlaneSolver.logDisplaySizeDebug('普通布局范围无效，保持子求解器尺寸', {
          planeIndex,
          constraint,
          effectiveConstraint: displayPolicy.effectiveConstraint,
          hintPointCount: hintPoints.length,
          hasImportedTopologyReference,
          sceneUnitScale: REFERENCE_PLANE_MM_TO_SCENE_UNIT_SCALE,
          marginScale: displayPolicy.marginScale,
          planeOrigin: ReferencePlaneSolver.toDebugVec3(plane.origin),
          xDir: ReferencePlaneSolver.toDebugVec3(plane.xDir),
          yDir: ReferencePlaneSolver.toDebugVec3(plane.yDir),
          originalDisplayWidth: plane.displayWidth ?? null,
          originalDisplayHeight: plane.displayHeight ?? null,
        });
        // 没有明确参考轮廓且模型包围盒不可用时，保持子求解器给出的尺寸，由渲染入口使用矩形兜底。
        return plane;
      }

      const adjustedOrigin = displayPolicy.recenterOrigin
        ? plane.origin.clone().addScaledVector(plane.xDir, extent.centerX).addScaledVector(plane.yDir, extent.centerY)
        : plane.origin.clone();
      const displayWidth = extent.width * displayPolicy.marginScale;
      const displayHeight = extent.height * displayPolicy.marginScale;

      ReferencePlaneSolver.logDisplaySizeDebug('普通布局范围与缩放', {
        planeIndex,
        constraint,
        effectiveConstraint: displayPolicy.effectiveConstraint,
        hintPointCount: hintPoints.length,
        hasImportedTopologyReference,
        sceneUnitScale: REFERENCE_PLANE_MM_TO_SCENE_UNIT_SCALE,
        marginScale: displayPolicy.marginScale,
        extent,
        displayWidth,
        displayHeight,
        originalPlaneOrigin: ReferencePlaneSolver.toDebugVec3(plane.origin),
        adjustedOrigin: ReferencePlaneSolver.toDebugVec3(adjustedOrigin),
        xDir: ReferencePlaneSolver.toDebugVec3(plane.xDir),
        yDir: ReferencePlaneSolver.toDebugVec3(plane.yDir),
      });

      return {
        ...plane,
        origin: adjustedOrigin,
        displayWidth,
        displayHeight,
      };
    });

    return {
      ...result,
      planes,
    };
  }

  /**
   * 按定义顺序解析引用实体。
   *
   * @param definition 特征定义。
   * @returns 已解析实体集合。
   */
  private resolveEntities(
    definition: PlaneFeatureDefinition,
  ): PlaneResolvedEntity[] {
    // 先按角色排序，再逐个解析，保证求解器输入稳定。
    const orderedRefs = [...definition.refs].sort((a, b) => {
      const rank = { first: 0, second: 1, third: 2 } as Record<string, number>;
      return (rank[a.role] ?? 999) - (rank[b.role] ?? 999);
    });
    return orderedRefs
      .map((ref) => this.controller.resolveReference(ref.entityRef))
      .filter((entity): entity is PlaneResolvedEntity => Boolean(entity));
  }

  /**
   * 兼容不同引用顺序的垂直约束求解。
   *
   * @param resolved 已解析实体。
   * @param originOnCurve 是否将原点投影到曲线上。
   * @returns 求解结果。
   */
  private solvePerpendicularConstraintWithOrderCompatibility(
    resolved: PlaneResolvedEntity[],
    originOnCurve: boolean,
    reverseOffset: boolean,
  ): PlaneFeatureResult {
    const surfacePlanePerpendicularResult = this.solveSurfacePlanePerpendicularConstraint(resolved, reverseOffset);
    if (surfacePlanePerpendicularResult) {
      return surfacePlanePerpendicularResult;
    }

    const pointTwoPlanePerpendicular = ReferencePlaneSolver.solvePerpendicularByPointAndTwoPlanes(resolved);
    if (pointTwoPlanePerpendicular) {
      return pointTwoPlanePerpendicular;
    }

    const directLinePerpendicularResult = ReferencePlaneSolver.solveLinePerpendicularConstraint(resolved);
    if (directLinePerpendicularResult) {
      return directLinePerpendicularResult;
    }

    const isPlaneLineOrder = Boolean(ReferencePlaneSolver.readPlane(resolved[0]) && ReferencePlaneSolver.readDirection(resolved[1]));
    if (isPlaneLineOrder) {
      // 旧数据或不同拾取顺序可能导致“面,在线后”，这里统一重排后复用线主导求解。
      const reordered: PlaneResolvedEntity[] = [resolved[1], resolved[0], ...resolved.slice(2)];
      const reorderedResult = ReferencePlaneSolver.solveLinePerpendicularConstraint(reordered);
      if (reorderedResult) {
        return reorderedResult;
      }
    }

    return ReferencePlaneSolver.solveNormal(resolved, originOnCurve);
  }

  /**
   * 求解“曲面相切 + 平面垂直”组合。
   *
   * @param resolved 已解析参考。
   * @param reverseOffset
   * @returns 成功命中该组合时返回欠定义结果，否则返回 `null`。
   */
  private solveSurfacePlanePerpendicularConstraint(
    resolved: PlaneResolvedEntity[],
    reverseOffset: boolean,
  ): PlaneFeatureResult | null {
    const referenceBundle = ReferencePlaneSolver.resolveSurfacePlaneReferenceBundle(resolved);
    if (!referenceBundle) {
      return null;
    }

    const frameCandidates = ReferencePlaneSolver.collectSurfacePlanePerpendicularCandidates(
      referenceBundle.surfaceEntity,
      referenceBundle.plane.normal,
    );
    const preferredNormalDirection = this.resolvePreferredPerpendicularNormalDirection(
      referenceBundle.plane.normal,
    );
    const selectedFrame = ReferencePlaneSolver.selectSurfacePlanePerpendicularCandidate(
      frameCandidates,
      reverseOffset,
      preferredNormalDirection,
    );
    if (!selectedFrame) {
      return ReferencePlaneSolver.buildErrorResult(
        PlaneSolveStatus.NoSolution,
        '当前曲面上不存在同时满足相切且垂直于所选平面的基准面',
        [{
          code: 'PLANE_SURFACE_PERPENDICULAR_NO_SOLUTION',
          message: '曲面局部法向与参考平面法向不存在正交解，无法构造满足条件的相切基准面',
          severity: ReferenceDiagnosticSeverity.Error,
        }],
      );
    }

    return ReferencePlaneSolver.buildSuccessResult({
      frameCandidates: [selectedFrame],
      statusText: '完全定义',
      availableConstraints: [PlaneConstraintType.Perpendicular],
      diagnostics: [{
        code: 'PLANE_SURFACE_PERPENDICULAR_READY',
        message: reverseOffset
          ? '已按反转等距切换到另一侧的相切垂直基准面'
          : '已按当前视图侧向与参考面法向的右手系方向选中默认的相切垂直基准面',
        severity: ReferenceDiagnosticSeverity.Info,
      }],
    });
  }

  /**
   * 解析“点 + 面 + 面”的垂直建面。
   *
   * @param resolved 已解析参考。
   * @returns 成功时返回结果，否则返回 `null`。
   */
  private static solvePerpendicularByPointAndTwoPlanes(resolved: PlaneResolvedEntity[]): PlaneFeatureResult | null {
    const points = resolved
      .map((entity) => ReferencePlaneSolver.readPoint(entity))
      .filter((item): item is THREE.Vector3 => Boolean(item));
    const planes = resolved
      .map((entity) => ReferencePlaneSolver.readPlane(entity))
      .filter((item): item is NonNullable<typeof item> => Boolean(item));

    if (points.length === 0 || planes.length < 2) {
      return null;
    }

    const point = points[0];
    const firstPlane = planes[0];
    const secondPlane = planes[1];
    const normalCandidate = firstPlane.normal.clone().cross(secondPlane.normal);
    if (normalCandidate.length() <= LINEAR_EPSILON) {
      return ReferencePlaneSolver.buildErrorResult(
        PlaneSolveStatus.UnderDefined,
        '两个平面平行时，经过点且同时垂直于两个面的基准面不唯一',
      );
    }

    return ReferencePlaneSolver.buildSuccessResult({
      frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(point, ReferencePlaneSolver.normalizeVec3(normalCandidate))],
      statusText: '完全定义',
      availableConstraints: [PlaneConstraintType.Perpendicular, PlaneConstraintType.Parallel, PlaneConstraintType.Coincident],
      diagnostics: [{
        code: 'PLANE_POINT_TWO_PLANE_PERPENDICULAR_READY',
        message: '已按“点 + 面 + 面垂直”构造基准面',
        severity: ReferenceDiagnosticSeverity.Info,
      }],
    });
  }

  /**
   * 求解平行约束。
   *
   * @param resolved 已解析实体。
   * @returns 求解结果。
   */
  private static solveParallel(resolved: PlaneResolvedEntity[]): PlaneFeatureResult {

      /**
       * 曲面 + 平面切到平行后，需要重新计算“与平面平行且与曲面相切”的支撑面。
       * 当前业务只保留默认第一解，不再把另一侧支撑层暴露为第二候选解。
       */
      const surfacePlaneReferences = ReferencePlaneSolver.resolveSurfacePlaneParallelReferences(resolved);
      if (surfacePlaneReferences) {
        const frameCandidates = ReferencePlaneSolver.buildSurfaceParallelSupportFrames(
          surfacePlaneReferences.surfaceEntity,
          surfacePlaneReferences.planeGeometry,
        );
        if (frameCandidates.length === 0) {
          return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.NoSolution, '曲面缺少可用于平行相切求解的顶点数据');
        }
        return ReferencePlaneSolver.buildSuccessResult({
          frameCandidates: [frameCandidates[0]],
          statusText: '完全定义',
          availableConstraints: [PlaneConstraintType.Perpendicular, PlaneConstraintType.Parallel],
          diagnostics: [{
            code: 'PLANE_SURFACE_PLANE_PARALLEL_READY',
            message: '已按所选平面法向重算曲面的默认平行相切基准面',
            severity: ReferenceDiagnosticSeverity.Info,
          }],
        });
      }


      const planeReferences = resolved
        .map((entity) => ReferencePlaneSolver.readPlane(entity))
        .filter((item): item is NonNullable<typeof item> => Boolean(item));
      const pointReferences = resolved
        .map((entity) => ReferencePlaneSolver.readPoint(entity))
        .filter((item): item is THREE.Vector3 => Boolean(item));

      const basePlane = planeReferences[0] ?? null;
      if (!basePlane) {
        return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.UnderDefined, '平行建面需要平面参考和点参考');
      }


      /**
       * 平行面定位语义：
       * 1. 结果面必须与所选面保持同一面内中心坐标；
       * 2. 只允许沿所选面法线方向平移；
       * 3. 因此生成原点不是输入点本身，而是“原面中心沿法线平移后的点”。
       */
      const translatePlanePointAlongNormal = (anchorPoint: THREE.Vector3): THREE.Vector3 => {
        const signedDistance = anchorPoint.clone().sub(basePlane.point).dot(basePlane.normal);
        return basePlane.point.clone().addScaledVector(basePlane.normal, signedDistance);
      };


      if (pointReferences.length >= 2) {
        const firstPoint = pointReferences[0];
        const secondPoint = pointReferences[1];
        const signedDistanceA = firstPoint.clone().sub(basePlane.point).dot(basePlane.normal);
        const signedDistanceB = secondPoint.clone().sub(basePlane.point).dot(basePlane.normal);

          /**
         * 当前业务语义：
         * 1. 点 → 点 时仍处于重合语义；
         * 2. 再选面切到平行模式后，结果面必须与所选面平行；
         * 3. 结果面的位置由第一点决定，但只沿所选面法线方向平移；
         * 4. 原面中心在面内两个轴上的坐标保持不变；
         * 5. 第二点仅用于维持“两点 + 面”组合语义，不再决定结果面中心。
         */
        const origin = translatePlanePointAlongNormal(firstPoint);
        const isSameParallelLayer = Math.abs(signedDistanceA - signedDistanceB) <= LINEAR_EPSILON;

          return ReferencePlaneSolver.buildSuccessResult({
          frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(origin, basePlane.normal)],
          statusText: '完全定义',
          availableConstraints: [PlaneConstraintType.Parallel, PlaneConstraintType.Coincident, PlaneConstraintType.Project],
          diagnostics: [{
            code: 'PLANE_TWO_POINT_PLANE_PARALLEL_READY',
            message: isSameParallelLayer
              ? '已按第一点将所选面沿法线平移后构造平行基准面'
              : '已按第一点将所选面沿法线平移到目标偏移层后构造平行基准面',
            severity: ReferenceDiagnosticSeverity.Info,
          }],
        });
      }

      const point = pointReferences[0] ?? null;
      if (!point) {
        return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.Inconsistent, '参考类型不满足平行建面要求');
      }
      return ReferencePlaneSolver.buildSuccessResult({
        frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(translatePlanePointAlongNormal(point), basePlane.normal)],
        statusText: '完全定义',
        availableConstraints: [PlaneConstraintType.Parallel, PlaneConstraintType.Coincident, PlaneConstraintType.Perpendicular],
      });
    }

  /**
   * 求解法向约束。
   *
   * @param resolved 已解析实体。
   * @param originOnCurve 是否将原点投影到曲线上。
   * @returns 求解结果。
   */
  private static solveNormal(resolved: PlaneResolvedEntity[], originOnCurve: boolean): PlaneFeatureResult {
      const first = resolved[0];
      const second = resolved[1];
      if (!first || !second) {
        return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.UnderDefined, '法向建面需要线性参考和点参考');
      }

      const dir = ReferencePlaneSolver.readDirection(first);
      const point = ReferencePlaneSolver.readPoint(second);
      if (!dir || !point) {
        return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.Inconsistent, '参考类型不满足法向建面要求');
      }

      const linePoint = ReferencePlaneSolver.readPoint(first) ?? point;
      const origin = originOnCurve ? ReferencePlaneSolver.projectPointOnLine(point, linePoint, dir) : point;
      return ReferencePlaneSolver.buildSuccessResult({
        frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(origin, dir)],
        statusText: '完全定义',
        availableConstraints: [PlaneConstraintType.Normal, PlaneConstraintType.Coincident],
        diagnostics: [{
          code: 'PLANE_NORMAL_READY',
          message: originOnCurve ? '原点已投影到曲线上' : '原点保持在输入点',
          severity: ReferenceDiagnosticSeverity.Info,
        }],
      });
    }

  /**
   * 求解投影到曲面/在曲面上的约束。
   *
   * @param resolved 已解析实体。
   * @param projectionType 投影类型。
   * @returns 求解结果。
   */
  private static solveOnSurfaceProject(
    resolved: PlaneResolvedEntity[],
    projectionType: PlaneProjectionType,
    tolerance: PlaneTolerancePolicy,
  ): PlaneFeatureResult {
      const referenceBundle = ReferencePlaneSolver.resolveSurfacePointReferenceBundle(resolved);
      if (!referenceBundle) {
        return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.UnderDefined, 'On Surface/Project 需要点和曲面两个参考');
      }

      const diagnostics: PlaneDiagnostic[] = [];
      let projectionCandidate: SurfaceProjectionCandidate | null = null;

      if (projectionType === PlaneProjectionType.AlongSketchNormal) {
        /**
         * 当前点参考还未持有“所属草图平面法向”语义，无法在内核侧可靠恢复 SolidWorks 的“沿草图法线”投影。
         * 在补齐点参考来源链路前，这里先显式回退到最近点投影，并通过诊断提示当前退化语义。
         */
        diagnostics.push({
          code: 'PLANE_ALONG_SKETCH_NORMAL_FALLBACK',
          message: '当前点参考缺少草图法线语义，已回退为最近点投影',
          severity: ReferenceDiagnosticSeverity.Warning,
        });
      }

      projectionCandidate = ReferencePlaneSolver.projectPointOnSurface(
        referenceBundle.surfaceEntity,
        referenceBundle.point,
      );
      if (!projectionCandidate) {
        return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.Inconsistent, '曲面参考缺少可用三角面，无法完成投影');
      }

      const acceptedDistance = Math.max(tolerance.projectionEpsilon, 1e-6);
      diagnostics.push({
        code: 'PLANE_ON_SURFACE_MODE',
        message: projectionType === PlaneProjectionType.NearestOnSurface
          ? '投影类型：最近点'
          : '投影类型：沿草图法线（当前回退为最近点）',
        severity: ReferenceDiagnosticSeverity.Info,
      });
      diagnostics.push({
        code: 'PLANE_ON_SURFACE_DISTANCE',
        message: `曲面投影偏差 ${Math.sqrt(Math.max(0, projectionCandidate.distanceSq)).toFixed(6)}，容差 ${acceptedDistance.toFixed(6)}`,
        severity: projectionCandidate.distanceSq <= acceptedDistance * acceptedDistance
          ? ReferenceDiagnosticSeverity.Info
          : ReferenceDiagnosticSeverity.Warning,
      });

      return ReferencePlaneSolver.buildSuccessResult({
        frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(
          projectionCandidate.point,
          projectionCandidate.normal,
        )],
        statusText: '完全定义',
        editableFields: [PlaneEditableField.ProjectionType],
        availableConstraints: [PlaneConstraintType.OnSurface, PlaneConstraintType.Project, PlaneConstraintType.Tangent],
        availableProjectionTypes: [PlaneProjectionType.NearestOnSurface, PlaneProjectionType.AlongSketchNormal],
        diagnostics,
      });
    }

  /**
   * 求解相切约束。
   *
   * @param resolved 已解析实体。
   * @returns 求解结果。
   */
  private solveTangent(
    resolved: PlaneResolvedEntity[],
    tolerance: PlaneTolerancePolicy,
  ): PlaneFeatureResult {
      const referenceBundle = ReferencePlaneSolver.resolveSurfacePointReferenceBundle(resolved);
      if (!referenceBundle) {
        return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.UnderDefined, '相切建面需要曲面参考和点参考');
      }

      const tangentCandidate = ReferencePlaneSolver.projectPointOnSurface(
        referenceBundle.surfaceEntity,
        referenceBundle.point,
      );
      if (!tangentCandidate) {
        return ReferencePlaneSolver.buildErrorResult(PlaneSolveStatus.Inconsistent, '曲面缺少可用三角面，无法计算切平面');
      }

      const acceptedDistance = Math.max(tolerance.linearEpsilon, 1e-5);
      if (tangentCandidate.distanceSq <= acceptedDistance * acceptedDistance) {
        return ReferencePlaneSolver.buildSuccessResult({
          frameCandidates: [ReferencePlaneSolver.buildPlaneFrame(
            tangentCandidate.point,
            tangentCandidate.normal,
          )],
          statusText: '完全定义',
          availableConstraints: [PlaneConstraintType.Tangent, PlaneConstraintType.OnSurface, PlaneConstraintType.Project],
          diagnostics: [{
            code: 'PLANE_TANGENT_READY',
            message: '已按曲面切点处的局部法向构造相切基准面',
            severity: ReferenceDiagnosticSeverity.Info,
          }],
        });
      }

      const tangentCandidates = this.orderSurfacePointTangentCandidates(
        ReferencePlaneSolver.collectSurfacePointTangentCandidates(
          referenceBundle.surfaceEntity,
          referenceBundle.point,
        ),
      );
      if (tangentCandidates.length === 0) {
        return ReferencePlaneSolver.buildErrorResult(
          PlaneSolveStatus.NoSolution,
          '未找到同时与曲面相切且经过所选点的基准面',
          [{
            code: 'PLANE_TANGENT_THROUGH_POINT_UNRESOLVED',
            message: `当前点到曲面的最近距离为 ${Math.sqrt(tangentCandidate.distanceSq).toFixed(6)}，且三角网格中未找到满足“点在切平面上”的候选切点`,
            severity: ReferenceDiagnosticSeverity.Error,
          }],
        );
      }

      // 统一只保留默认首解，避免 UI 暴露候选解切换并保持与 SolidWorks 默认行为一致。
      const defaultCandidate = tangentCandidates[0];
      if (!defaultCandidate) {
        return ReferencePlaneSolver.buildErrorResult(
          PlaneSolveStatus.NoSolution,
          '未找到可用的默认相切基准面',
          [{
            code: 'PLANE_TANGENT_DEFAULT_CANDIDATE_MISSING',
            message: '曲面相切候选为空，无法确定默认解',
            severity: ReferenceDiagnosticSeverity.Error,
          }],
        );
      }

      return ReferencePlaneSolver.buildSuccessResult({
        frameCandidates: [defaultCandidate.frame],
        statusText: '完全定义',
        availableConstraints: [PlaneConstraintType.Tangent, PlaneConstraintType.OnSurface, PlaneConstraintType.Project],
        diagnostics: [{
          code: 'PLANE_TANGENT_THROUGH_POINT_READY',
          message: '已找到并采用默认相切基准面',
          severity: ReferenceDiagnosticSeverity.Info,
        }],
      });
    }

}
